// VideoCall Component - Video and Audio calls with controls
// Based on documentation from docs/04_VIDEO_AUDIO_CALLS.md

import React, { useState, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { showToast } from '../utils/notifications';
import { VIDEO_CONSTRAINTS, AUDIO_CONSTRAINTS } from '../utils/constants';
import { addBg } from '../utils/backgroundEffects';
import '../styles/VideoCall.css';

const VideoCall = React.forwardRef(({ connectionManager, currentUser, users, isVisible = true, onIncomingCall, onCallEvent, onStateChange, onBandwidthUpdate, hideControls = false, bgSettings = { enabled: false, mode: 'blur', selectedIndex: 0 } }, ref) => {
  // Debug logs (commented out to reduce console noise)
  // console.log('🔴🔴🔴 [VideoCall] COMPONENT RENDERING - isVisible:', isVisible, 'currentUser:', currentUser?.peerId);
  // console.log('🔴 [VideoCall] Socket status:', {
  //   hasConnectionManager: !!connectionManager,
  //   hasSocket: !!connectionManager?.socket,
  //   socketConnected: connectionManager?.socket?.connected,
  //   socketId: connectionManager?.socket?.id
  // });
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [micGain, setMicGain] = useState(100);
  const [pendingCall, setPendingCall] = useState(null);
  const [peerMetadata, setPeerMetadata] = useState({}); // Store userName per peerId
  const [streamStats, setStreamStats] = useState({}); // Bitrate stats per peerId
  const [, setLocalUploadStats] = useState({ sent: 0 }); // Local upload stats
  const [totalBandwidth, setTotalBandwidth] = useState({ sent: 0, received: 0 });
  
  // Group call state
  const [groupCallActive, setGroupCallActive] = useState(false);
  const [groupCallType, setGroupCallType] = useState(null); // 'video' or 'audio'
  const [groupCallInitiator, setGroupCallInitiator] = useState(null);
  const [showJoinButton, setShowJoinButton] = useState(false);
  const [isOnCall, setIsOnCall] = useState(false);
  const [rejectedGroupCallId, setRejectedGroupCallId] = useState(null); // Track rejected group calls
  const [audioLevels, setAudioLevels] = useState({}); // Voice activity levels per peerId (0-1)
  const [isPIPActive, setIsPIPActive] = useState(false); // Browser Picture-in-Picture running
  const [disconnectedPeers, setDisconnectedPeers] = useState({}); // Track peers who left call - show red slash until all drop
  
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaCallsRef = useRef({});
  const mountCountRef = useRef(0);
  const statsIntervalRef = useRef(null);
  const lastStatsRef = useRef({});
  const lastFrameTimeRef = useRef({}); // Track last frame received time per peerId
  const staleStreamCheckRef = useRef(null); // Interval for checking stale streams
  const callIceDisconnectTimersRef = useRef({}); // Per-peer ICE disconnect grace timers for calls
  const callPeerTypesRef = useRef({}); // Track callType per peerId for ICE recovery re-calls
  const pendingCallsRef = useRef({}); // { peerId: callType } - calls queued while peer transport was down
  const disconnectedPeersRef = useRef({}); // Ref mirror of disconnectedPeers for event handlers
  const intentionalCloseRef = useRef(false); // True when user pressed End Call (suppress auto re-call)
  const callCloseRetryTimersRef = useRef({}); // Per-peer re-call timers after unexpected call close
  const groupCallTypeRef = useRef(null); // Ref mirror of groupCallType for event handlers
  
  // Refs to track current state for event handlers (avoid stale closures)
  const isCallActiveRef = useRef(false);
  const localStreamRef = useRef(null);
  const videoEnabledRef = useRef(false);
  const audioEnabledRef = useRef(false);
  const currentUserRef = useRef(null);
  const onIncomingCallRef = useRef(null);
  const groupCallActiveRef = useRef(false);
  const groupCallInitiatorRef = useRef(null);
  const isOnCallRef = useRef(false);
  const lastAssignedStreamRef = useRef({});
  const voiceAnalysersRef = useRef({}); // { id: { analyser, source } } for voice activity
  const voiceContextRef = useRef(null); // Shared AudioContext for voice detection
  const voiceSampleIntervalRef = useRef(null);
  // PIP: canvas compositing pipeline
  const pipCanvasRef = useRef(null);      // Hidden <canvas> where all tiles are drawn
  const pipVideoRef = useRef(null);       // Hidden <video> fed by canvas stream
  const pipDrawIntervalRef = useRef(null); // setInterval handle for draw loop
  const pipStreamRef = useRef(null);      // canvas.captureStream() result
  const isPIPActiveRef = useRef(false);   // Ref mirror of isPIPActive for use inside effects
  const usersRef = useRef(null);          // Ref mirror of users prop (avoids stale closure in draw fn)
  const peerMetadataRef = useRef({});     // Ref mirror of peerMetadata state
  const peersOnCallRef = useRef({});      // { peerId: true } — tracks who is on a call via call-state-update events
  // Virtual background processing
  const rawCameraStreamRef = useRef(null); // original unprocessed camera stream
  const bgProcessorRef = useRef(null);     // { stream, stop } from addBg()
  const bgSettingsRef = useRef(bgSettings);

  // Helper: properly clean up a video element to prevent frozen frames
  const cleanupVideoElement = (peerId) => {
    const el = remoteVideoRefs.current[peerId];
    if (el) {
      try {
        el.srcObject = null;
        el.load();
      } catch (_) {}
      delete remoteVideoRefs.current[peerId];
    }
  };

  // Helper: attach a remote stream to a video element with retry + play()
  // Works for both callPeer and answerCall stream handlers
  const attachRemoteStream = (peerId, remoteStream, call) => {
    const attachWithRetry = (attempt = 1) => {
      const videoEl = remoteVideoRefs.current[peerId];
      if (!videoEl) {
        if (attempt <= 5) {
          console.log(`[VideoCall] Video element not found for ${peerId}, retry ${attempt}/5 in 1s`);
          setTimeout(() => attachWithRetry(attempt + 1), 1000);
        } else {
          console.warn('[VideoCall] ⚠️ Video element not found after 5 retries:', peerId);
        }
        return;
      }

      // Clear lastAssigned so useEffect doesn't skip this assignment
      delete lastAssignedStreamRef.current[peerId];
      videoEl.srcObject = remoteStream;
      videoEl.muted = false;
      videoEl.volume = 1;
      videoEl.play?.().catch(() => {});

      // Set up actual frame rendering detection
      if (typeof videoEl.requestVideoFrameCallback === 'function') {
        const onFrame = () => {
          lastFrameTimeRef.current[peerId] = Date.now();
          videoEl.requestVideoFrameCallback(onFrame);
        };
        videoEl.requestVideoFrameCallback(onFrame);
      } else if (call?.peerConnection) {
        const pc = call.peerConnection;
        const checkInterval = setInterval(async () => {
          try {
            const stats = await pc.getStats();
            let gotMedia = false;
            stats.forEach(report => {
              if (report.type === 'inbound-rtp' && report.packetsReceived > 0) {
                gotMedia = true;
              }
            });
            if (gotMedia) {
              lastFrameTimeRef.current[peerId] = Date.now();
            }
          } catch(_) {}
        }, 2000);
        const origClose = call.close.bind(call);
        call.close = function() {
          clearInterval(checkInterval);
          return origClose();
        };
      }

      // Check if video is actually playing after 3 seconds
      setTimeout(() => {
        if (videoEl.srcObject === remoteStream && (videoEl.paused || videoEl.readyState < 2)) {
          console.warn(`[VideoCall] Video appears stuck for ${peerId}, attempting refresh...`);
          videoEl.srcObject = null;
          setTimeout(() => {
            delete lastAssignedStreamRef.current[peerId];
            videoEl.srcObject = remoteStream;
            videoEl.play().catch(e => console.warn('[VideoCall] Play failed:', e));
          }, 100);
        }
      }, 3000);

      console.log('[VideoCall] ✅ Remote video element attached for:', peerId);
    };

    attachWithRetry();
  };

  // Helper: mark a peer as disconnected (red slash icons) and remove their stream
  const handlePeerStreamRemoved = (peerId) => {
    cleanupVideoElement(peerId);
    delete lastAssignedStreamRef.current[peerId];
    setRemoteStreams(prev => {
      const updated = { ...prev };
      delete updated[peerId];
      return updated;
    });
    // Mark as disconnected so tile shows red slash icons
    setDisconnectedPeers(prev => ({ ...prev, [peerId]: true }));
  };

  // Keep refs in sync with state for event handlers
  useEffect(() => {
    isCallActiveRef.current = isCallActive;
    localStreamRef.current = localStream;
    videoEnabledRef.current = videoEnabled;
    audioEnabledRef.current = audioEnabled;
    currentUserRef.current = currentUser;
    onIncomingCallRef.current = onIncomingCall;
    groupCallActiveRef.current = groupCallActive;
    groupCallInitiatorRef.current = groupCallInitiator;
    isOnCallRef.current = isOnCall;
    bgSettingsRef.current = bgSettings;
  }, [isCallActive, localStream, videoEnabled, audioEnabled, currentUser, onIncomingCall, groupCallActive, groupCallInitiator, isOnCall, bgSettings]);

  // Keep disconnectedPeers ref in sync for event handlers
  useEffect(() => { disconnectedPeersRef.current = disconnectedPeers; }, [disconnectedPeers]);

  // Keep PIP draw refs in sync
  useEffect(() => { usersRef.current = users; }, [users]);
  useEffect(() => { peerMetadataRef.current = peerMetadata; }, [peerMetadata]);

  // Apply or swap background effect live when settings change while a call is active
  useEffect(() => {
    const active = isOnCall || isCallActive || groupCallActive;
    const hasRaw = !!rawCameraStreamRef.current;
    console.log('[VideoCall] 🎨 bgSettings changed:', bgSettings, 'active:', active, 'hasRaw:', hasRaw);
    if (active && hasRaw) {
      applyBackgroundLive(bgSettings);
    } else if (!hasRaw) {
      console.warn('[VideoCall] 🎨 Cannot apply background live - no raw camera stream captured yet (rawCameraStreamRef is null). Was the camera started via a path that calls maybeApplyBackground?');
    }
  }, [bgSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track component lifecycle - RUNS IMMEDIATELY ON MOUNT
  useEffect(() => {
    mountCountRef.current += 1;
    console.log('🔴🔴🔴 [VideoCall] 🎬 COMPONENT MOUNTED (count:', mountCountRef.current, ')');
    console.log('🔴🔴🔴 [VideoCall] Props:', { 
      hasConnectionManager: !!connectionManager, 
      hasCurrentUser: !!currentUser,
      currentUserId: currentUser?.peerId,
      isVisible,
      hasSocket: !!connectionManager?.socket,
      socketConnected: connectionManager?.socket?.connected
    });
    return () => {
      console.log('🔴🔴🔴 [VideoCall] 🛑 COMPONENT UNMOUNTING');
      if (bgProcessorRef.current?.stop) {
        try { bgProcessorRef.current.stop(); } catch (_) {}
      }
      bgProcessorRef.current = null;
      rawCameraStreamRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  

  // Log render state
  // console.log('[VideoCall] 🔄 Component render - State:', {
  //   isVisible,
  //   isCallActive,
  //   videoEnabled,
  //   audioEnabled,
  //   hasLocalStream: !!localStream,
  //   remoteStreamCount: Object.keys(remoteStreams).length,
  //   remoteStreamPeers: Object.keys(remoteStreams),
  //   hasConnectionManager: !!connectionManager,
  //   connectionManagerId: connectionManager?.getPeerId?.()
  // });

  // === VOICE ACTIVITY DETECTION ===
  const createVoiceAnalyser = (stream, id) => {
    // Clean up existing analyser for this id
    cleanupVoiceAnalyser(id);

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack || audioTrack.readyState !== 'live') return;

    try {
      if (!voiceContextRef.current || voiceContextRef.current.state === 'closed') {
        voiceContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = voiceContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      // Do NOT connect to destination — avoid echo

      voiceAnalysersRef.current[id] = { analyser, source };
    } catch (e) {
      console.warn('[VideoCall] Failed to create voice analyser for', id, e);
    }
  };

  const cleanupVoiceAnalyser = (id) => {
    const entry = voiceAnalysersRef.current[id];
    if (entry) {
      try { entry.source.disconnect(); } catch (_) {}
      delete voiceAnalysersRef.current[id];
    }
  };

  // Auto-create/cleanup voice analysers when streams change
  useEffect(() => {
    // Local stream
    if (localStream && localStream.getAudioTracks().length > 0) {
      createVoiceAnalyser(localStream, 'local');
    } else {
      cleanupVoiceAnalyser('local');
    }

    // Remote streams
    const currentRemoteIds = Object.keys(remoteStreams);
    const existingIds = Object.keys(voiceAnalysersRef.current).filter(id => id !== 'local');

    // Add new analysers
    currentRemoteIds.forEach(peerId => {
      const stream = remoteStreams[peerId];
      if (stream && stream.getAudioTracks().length > 0 && !voiceAnalysersRef.current[peerId]) {
        createVoiceAnalyser(stream, peerId);
      }
    });

    // Remove stale analysers
    existingIds.forEach(id => {
      if (!currentRemoteIds.includes(id)) {
        cleanupVoiceAnalyser(id);
      }
    });
  }, [localStream, remoteStreams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sample audio levels at ~12fps for smooth visual feedback without excessive renders
  useEffect(() => {
    if (!isCallActive && !isOnCall) {
      // Clear levels and stop sampling when not on call
      if (voiceSampleIntervalRef.current) {
        clearInterval(voiceSampleIntervalRef.current);
        voiceSampleIntervalRef.current = null;
      }
      setAudioLevels({});
      return;
    }

    voiceSampleIntervalRef.current = setInterval(() => {
      const analysers = voiceAnalysersRef.current;
      if (Object.keys(analysers).length === 0) return;

      const levels = {};

      Object.entries(analysers).forEach(([id, { analyser }]) => {
        const bufLen = analyser.fftSize;
        const dataArray = new Uint8Array(bufLen);
        analyser.getByteTimeDomainData(dataArray);
        // RMS of waveform deviation from center (128) — directly represents amplitude
        let sum = 0;
        for (let i = 0; i < bufLen; i++) {
          const sample = (dataArray[i] - 128) / 128; // normalize to -1..1
          sum += sample * sample;
        }
        const rms = Math.sqrt(sum / bufLen);
        // Threshold to filter ambient noise, then scale for visual impact
        levels[id] = rms < 0.01 ? 0 : Math.min(1, rms * 4);
      });

      setAudioLevels(prev => {
        // Only update if values actually changed (avoid unnecessary re-renders)
        const changed = Object.keys(levels).some(id =>
          Math.abs((prev[id] || 0) - levels[id]) > 0.02
        ) || Object.keys(prev).some(id => !(id in levels));
        return changed ? levels : prev;
      });
    }, 80); // ~12fps

    return () => {
      if (voiceSampleIntervalRef.current) {
        clearInterval(voiceSampleIntervalRef.current);
        voiceSampleIntervalRef.current = null;
      }
    };
  }, [isCallActive, isOnCall]);

  // Cleanup voice detection on unmount
  useEffect(() => {
    const analysersRef = voiceAnalysersRef;
    const ctxRef = voiceContextRef;
    const intervalRef = voiceSampleIntervalRef;
    return () => {
      Object.keys(analysersRef.current).forEach(id => {
        const entry = analysersRef.current[id];
        if (entry) { try { entry.source.disconnect(); } catch (_) {} }
        delete analysersRef.current[id];
      });
      if (ctxRef.current && ctxRef.current.state !== 'closed') {
        try { ctxRef.current.close(); } catch (_) {}
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  // === END VOICE ACTIVITY DETECTION ===

  // Broadcast group call start to all users
  const broadcastGroupCallStart = useRef((callType) => {
    const cm = connectionManager;
    const user = currentUserRef.current;
    
    if (!cm) {
      console.error('[VideoCall] No connectionManager available');
      return;
    }
    
    const socket = cm.socket;
    if (!socket) {
      console.error('[VideoCall] No socket available for broadcasting');
      return;
    }
    
    if (!socket.connected) {
      console.error('[VideoCall] Socket not connected! Cannot broadcast group call');
      return;
    }
    
    const broadcastData = {
      roomId: cm.roomId,
      initiatorId: user?.peerId,
      initiatorName: user?.peerName || user?.userName || user?.initial || 'User',
      callType: callType
    };
    
    console.log('[VideoCall] Broadcasting group call start:', broadcastData);
    socket.emit('group-call:start', broadcastData);
    console.log('[VideoCall] group-call:start event emitted successfully');
  });
  
  // Update ref when connectionManager changes
  useEffect(() => {
    broadcastGroupCallStart.current = (callType) => {
      const cm = connectionManager;
      const user = currentUserRef.current;
      
      if (!cm) {
        console.error('[VideoCall] No connectionManager available');
        return;
      }
      
      const socket = cm.socket;
      if (!socket) {
        console.error('[VideoCall] No socket available for broadcasting');
        return;
      }
      
      if (!socket.connected) {
        console.error('[VideoCall] Socket not connected! Cannot broadcast group call');
        return;
      }
      
      const broadcastData = {
        roomId: cm.roomId,
        initiatorId: user?.peerId,
        initiatorName: user?.peerName || user?.userName || user?.initial || 'User',
        callType: callType
      };
      
      console.log('[VideoCall] Broadcasting group call start:', broadcastData);
      socket.emit('group-call:start', broadcastData);
      console.log('[VideoCall] group-call:start event emitted successfully');
    };
  }, [connectionManager]);
  
  // Broadcast group call end to all users
  const broadcastGroupCallEnd = () => {
    if (!connectionManager) return;
    
    const socket = connectionManager.socket;
    if (!socket) return;
    
    console.log('[VideoCall] Broadcasting group call end');
    socket.emit('group-call:end', {
      roomId: connectionManager.roomId,
      peerId: currentUser?.peerId
    });
  };


  useEffect(() => {
    console.log('[VideoCall] 🎧 Peer Call Listeners useEffect RUNNING - connectionManager:', !!connectionManager, 'peerId:', connectionManager?.getPeerId?.());
    
    if (!connectionManager) {
      console.warn('[VideoCall] ⚠️ No connectionManager! Will wait for it...');
      return;
    }
    
    console.log('[VideoCall] ✅ ConnectionManager available - Setting up peer call listeners...');
    
    // === GROUP CALL SOCKET LISTENERS ===
    // Attach socket listeners for group calls
    const socket = connectionManager.socket;
    console.log('[VideoCall] 🟡 Checking socket for group call listeners - hasSocket:', !!socket, 'connected:', socket?.connected);
    
    // Listen for group call start notifications
    const handleGroupCallStart = (data) => {
      console.log('[VideoCall] ✅ =============== RECEIVED group-call:started EVENT ===============');
      console.log('[VideoCall] Event data:', JSON.stringify(data, null, 2));
      console.log('[VideoCall] Group call started by:', data.initiatorName, 'Type:', data.callType);
      
      const user = currentUserRef.current;
      console.log('[VideoCall] Current user from ref:', user?.peerId, 'Initiator:', data.initiatorId);
      
      // Always set group call state so UI reflects the active call
      setGroupCallActive(true);
      setGroupCallType(data.callType);
      setGroupCallInitiator(data.initiatorId);
      groupCallActiveRef.current = true;
      groupCallTypeRef.current = data.callType;
      groupCallInitiatorRef.current = data.initiatorId;
      
      // If I'm already on the call, skip showing popup
      if (isOnCallRef.current) {
        console.log('[VideoCall] Already on call, skipping notification');
        return;
      }
      
      // If I'm the initiator AND I started this call (not a refresh), skip popup
      // The group-call:status handler will handle the refresh case
      if (data.initiatorId === user?.peerId && isCallActiveRef.current) {
        console.log('[VideoCall] I am the active initiator, skipping notification');
        return;
      }
      
      console.log('[VideoCall] Setting up group call notification popup...');
      
      // Show notification popup
      const isMe = data.initiatorId === user?.peerId;
      setPendingCall({
        callType: data.callType,
        callerName: isMe ? 'Your Group Call' : data.initiatorName,
        isGroupCall: true,
        initiatorId: data.initiatorId
      });
      setShowJoinButton(true);
      
      console.log('[VideoCall] Pending call set, should show popup now');
      
      // Notify parent to show video panel
      const callback = onIncomingCallRef.current;
      if (callback) {
        callback({ peerId: data.initiatorId, callType: data.callType });
      }
    };
    
    // Listen for group call end notifications
    const handleGroupCallEnd = (data) => {
      console.log('[VideoCall] ❌ GROUP CALL ENDED EVENT RECEIVED:', data);
      console.log('[VideoCall] Ended by initiator:', data.endedByInitiator);
      
      // If initiator ended the call, FORCE END for everyone
      if (data.endedByInitiator) {
        console.log('[VideoCall] 🛑 INITIATOR ENDED CALL - FORCE ENDING FOR ALL');
        
        // Stop local stream
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
          setLocalStream(null);
          localStreamRef.current = null;
        }
        
        // Close all calls
        Object.values(mediaCallsRef.current).forEach(call => {
          try { call.close(); } catch (e) {}
        });
        mediaCallsRef.current = {};
        
        // Clean up all remote video elements
        Object.keys(remoteVideoRefs.current).forEach(pid => cleanupVideoElement(pid));
        
        // Clear all remote streams and disconnected peers
        setRemoteStreams({});
        setDisconnectedPeers({});
        
        // Reset all call states
        setIsCallActive(false);
        setIsOnCall(false);
        setVideoEnabled(false);
        setAudioEnabled(false);
        setGroupCallActive(false);
        setGroupCallType(null);
        groupCallTypeRef.current = null;
        setGroupCallInitiator(null);
        setShowJoinButton(false);
        setPendingCall(null);
        
        // Update refs
        isCallActiveRef.current = false;
        isOnCallRef.current = false;
        videoEnabledRef.current = false;
        audioEnabledRef.current = false;
        groupCallActiveRef.current = false;
        groupCallInitiatorRef.current = null;
        // Clear all on-call tracking — the server confirmed nobody remains on the call
        peersOnCallRef.current = {};
        
        showToast('Group call ended', 'info');
      } else {
        console.log('[VideoCall] Non-initiator left the call');
        // Individual user left, don't do anything
      }
    };
    
    if (socket) {
      console.log('🟢🟢🟢 [VideoCall] [Socket Listeners] ✅ ATTACHING group call listeners');
      console.log('🟢 [VideoCall] [Socket Listeners] Socket ID:', socket.id);
      console.log('🟢 [VideoCall] [Socket Listeners] Socket connected:', socket.connected);
      console.log('🟢 [VideoCall] [Socket Listeners] Room ID:', connectionManager.roomId);
      
      socket.on('group-call:started', handleGroupCallStart);
      socket.on('group-call:ended', handleGroupCallEnd);
      
      console.log('🟢🟢🟢 [VideoCall] [Socket Listeners] ✅ LISTENERS ATTACHED SUCCESSFULLY');

      // Late joiner safety: if the server already has an active group call, request status now.
      // This avoids missing the group-call:started event due to listener timing.
      try {
        socket.emit('group-call:status', { roomId: connectionManager.roomId }, (resp) => {
          console.log('[VideoCall] 🔍 Checking group call status on mount:', resp);
          if (!resp || !resp.status) return;
          if (resp.active && resp.groupCall) {
            const user = currentUserRef.current;
            const isInitiator = resp.groupCall.initiatorId === user?.peerId;
            
            console.log('[VideoCall] 📞 Active group call detected!', {
              initiator: resp.groupCall.initiatorId,
              me: user?.peerId,
              isInitiator,
              callType: resp.groupCall.callType,
              alreadyOnCall: isOnCallRef.current
            });
            
            setGroupCallActive(true);
            setGroupCallType(resp.groupCall.callType);
            setGroupCallInitiator(resp.groupCall.initiatorId);
            groupCallActiveRef.current = true;
            groupCallTypeRef.current = resp.groupCall.callType;
            groupCallInitiatorRef.current = resp.groupCall.initiatorId;
            
            // CRITICAL: If already on the call, don't show join button or popup
            if (isOnCallRef.current) {
              console.log('[VideoCall] ✅ Already on call - NOT showing join button');
              setShowJoinButton(false);
              return;
            }
            
            // Check if user already rejected this group call
            const callId = `${resp.groupCall.initiatorId}_${resp.groupCall.callType}`;
            if (rejectedGroupCallId === callId) {
              console.log('[VideoCall] 🚫 User previously rejected this call - NOT showing popup');
              setShowJoinButton(true);
              return;
            }
            
            // Show join button and popup for any user not on the call (including initiator after refresh)
            console.log('[VideoCall] 🟢 Not on call - showing join button. isInitiator:', isInitiator);
            setShowJoinButton(true);
            setPendingCall({
              isGroupCall: true,
              callType: resp.groupCall.callType,
              initiatorId: resp.groupCall.initiatorId,
              callerName: isInitiator ? 'Your Group Call' : 'Group Call'
            });
          } else {
            console.log('[VideoCall] No active group call detected');
          }
        });
      } catch (_) {}
    } else {
      console.warn('� [VideoCall] [Socket Listeners] No socket available - group calls will not work!');
    }
    // === END GROUP CALL SOCKET LISTENERS ===

    // Listen for incoming video calls via ConnectionManager events
    const handleIncomingVideoCall = ({ call, peerId, metadata }) => {
      console.log('[VideoCall] 📞 Incoming VIDEO call from:', peerId, 'metadata:', metadata);
      
      // Store caller's name with peerName prioritized over server-assigned userName
      const userFromList = users?.find(u => u.peerId === peerId);
      const callerName = metadata?.peerName ||
                        metadata?.userName || 
                        metadata?.initial ||
                        userFromList?.peerName || 
                        userFromList?.userName || 
                        userFromList?.initial ||
                        'User';
      
      setPeerMetadata(prev => ({ ...prev, [peerId]: { userName: callerName } }));
      
      const existingCall = mediaCallsRef.current[peerId];
      
      // AUTO-ACCEPT if we're the initiator of an active group call
      // This prevents the initiator from seeing an accept popup when others join
      const isGroupActive = !!groupCallActiveRef.current;
      const initiatorPeerId = groupCallInitiatorRef.current;
      const myPeerId = currentUserRef.current?.peerId;
      const iAmInitiator = initiatorPeerId && myPeerId && initiatorPeerId === myPeerId;

      if (isGroupActive && iAmInitiator) {
        console.log('[VideoCall] 🚀 Auto-accepting call - I am the group call initiator');
        
        // Answer immediately without showing popup
        call.answer(localStreamRef.current);
        
        // CRITICAL: Store call reference so stopAllCalls can close it
        mediaCallsRef.current[peerId] = call;
        
        // Start monitoring WebRTC stats for this call
        startStatsMonitoring(call, peerId);
        
        call.on('stream', (remoteStream) => {
          console.log('[VideoCall] ✅ Received remote stream from:', peerId);
          lastFrameTimeRef.current[peerId] = Date.now();
          setRemoteStreams(prev => ({
            ...prev,
            [peerId]: remoteStream
          }));
          // Peer is back — clear disconnected state
          setDisconnectedPeers(prev => {
            if (!prev[peerId]) return prev;
            const updated = { ...prev };
            delete updated[peerId];
            return updated;
          });
          // Attach with retry + play() + frame tracking
          attachRemoteStream(peerId, remoteStream, call);
        });
        
        call.on('close', () => {
          console.log('[VideoCall] Call closed by:', peerId);
          handlePeerStreamRemoved(peerId);
          delete mediaCallsRef.current[peerId];
          delete lastFrameTimeRef.current[peerId];
        });
        
        return; // Exit early, don't show popup
      }
      
      console.log('[VideoCall] Incoming VIDEO call metadata analysis:', {
        peerId,
        metadata,
        userFromList,
        resolvedName: callerName
      });
      
      // Check if we already have an active call with this peer
      if (existingCall) {
        console.log('[VideoCall] Already have call with peer:', peerId, '- closing old call and accepting new one');
        try {
          existingCall.close();
        } catch (e) {
          console.warn('[VideoCall] Error closing old call:', e);
        }
        delete mediaCallsRef.current[peerId];
        
        // Clean up old stream and tracking to prevent stale detection
        setRemoteStreams(prev => {
          const updated = { ...prev };
          delete updated[peerId];
          return updated;
        });
        delete lastFrameTimeRef.current[peerId];
        if (lastStatsRef.current[peerId]) {
          delete lastStatsRef.current[peerId];
        }
        if (statsIntervalRef.current?.[peerId]) {
          clearInterval(statsIntervalRef.current[peerId]);
          delete statsIntervalRef.current[peerId];
        }
        console.log('[VideoCall] Cleaned up old call state for:', peerId);
      }

      // Store the NEW call reference for this peer (after cleaning up any old call)
      mediaCallsRef.current[peerId] = call;
      
      // Notify parent component to show video panel
      if (onIncomingCall) {
        onIncomingCall({ peerId, callType: 'video' });
      }
      
      // Check if user is already on the call
      const iAmOnCall = isOnCallRef.current || isCallActiveRef.current;
      
      console.log('[VideoCall] Incoming call analysis:', {
        callerName,
        peerId,
        isGroupActive,
        iAmOnCall,
        hasPendingCall: !!pendingCall
      });
      
      // If I'm already on call, auto-answer without showing popup
      if (iAmOnCall) {
        console.log('[VideoCall] ✅ Already on call - auto-answering incoming call from:', callerName);
        answerCall(call);
        return;
      }
      
      // If group call is active but I haven't joined, check if user rejected it
      if (isGroupActive) {
        const initiator = groupCallInitiatorRef.current;
        const callId = `${initiator}_video`;
        if (rejectedGroupCallId === callId) {
          console.log('[VideoCall] 🚫 User rejected this group call - auto-answer stream but NO popup');
          answerCall(call);
          return;
        }
        console.log('[VideoCall] Group call active but not joined - auto-answering to receive stream from:', callerName);
        answerCall(call);
        return;
      }
      
      // Otherwise show popup (only for direct calls or first group call notification)
      console.log('[VideoCall] Showing popup for incoming call from:', callerName);
      setPendingCall({ call, peerId, callerName, callType: 'video' });
    };

    // Listen for incoming audio calls via ConnectionManager events
    const handleIncomingAudioCall = ({ call, peerId, metadata }) => {
      console.log('[VideoCall] 📞 Incoming AUDIO call from:', peerId, 'metadata:', metadata);
      
      // Store caller's name with peerName prioritized over server-assigned userName
      const userFromList = users?.find(u => u.peerId === peerId);
      const callerName = metadata?.peerName ||
                        metadata?.userName || 
                        metadata?.initial ||
                        userFromList?.peerName ||
                        userFromList?.userName ||
                        userFromList?.initial ||
                        'User';
      
      setPeerMetadata(prev => ({ ...prev, [peerId]: callerName }));

      const existingCall = mediaCallsRef.current[peerId];

      // AUTO-ACCEPT if we're the initiator of an active group call
      const isGroupActive = !!groupCallActiveRef.current;
      const initiatorPeerId = groupCallInitiatorRef.current;
      const myPeerId = currentUserRef.current?.peerId;
      const iAmInitiator = initiatorPeerId && myPeerId && initiatorPeerId === myPeerId;

      if (isGroupActive && iAmInitiator) {
        console.log('[VideoCall] 🚀 Auto-accepting audio call - I am the group call initiator');
        
        call.answer(localStreamRef.current);
        
        // CRITICAL: Store call reference so stopAllCalls can close it
        mediaCallsRef.current[peerId] = call;
        
        // Start monitoring WebRTC stats for this call
        startStatsMonitoring(call, peerId);
        
        call.on('stream', (remoteStream) => {
          console.log('[VideoCall] ✅ Received remote audio stream from:', peerId);
          lastFrameTimeRef.current[peerId] = Date.now();
          setRemoteStreams(prev => ({
            ...prev,
            [peerId]: remoteStream
          }));
          // Peer is back — clear disconnected state
          setDisconnectedPeers(prev => {
            if (!prev[peerId]) return prev;
            const updated = { ...prev };
            delete updated[peerId];
            return updated;
          });
          // Attach with retry + play() + frame tracking
          attachRemoteStream(peerId, remoteStream, call);
        });
        
        call.on('close', () => {
          console.log('[VideoCall] Audio call closed by:', peerId);
          handlePeerStreamRemoved(peerId);
          delete mediaCallsRef.current[peerId];
          delete lastFrameTimeRef.current[peerId];
        });
        
        return; // Exit early
      }
      
      console.log('[VideoCall] Incoming AUDIO call metadata analysis:', {
        peerId,
        metadata,
        userFromList,
        resolvedName: callerName
      });
      
      setPeerMetadata(prev => ({ ...prev, [peerId]: { userName: callerName } }));
      
      // Check if we already have an active call with this peer
      if (existingCall) {
        console.log('[VideoCall] Already have call with peer:', peerId, '- closing old call and accepting new one');
        try {
          existingCall.close();
        } catch (e) {
          console.warn('[VideoCall] Error closing old call:', e);
        }
        delete mediaCallsRef.current[peerId];
        
        // Clean up old stream and tracking to prevent stale detection
        setRemoteStreams(prev => {
          const updated = { ...prev };
          delete updated[peerId];
          return updated;
        });
        delete lastFrameTimeRef.current[peerId];
        if (lastStatsRef.current[peerId]) {
          delete lastStatsRef.current[peerId];
        }
        if (statsIntervalRef.current?.[peerId]) {
          clearInterval(statsIntervalRef.current[peerId]);
          delete statsIntervalRef.current[peerId];
        }
        console.log('[VideoCall] Cleaned up old call state for:', peerId);
      }
      
      // Notify parent component to show video panel
      if (onIncomingCall) {
        onIncomingCall({ peerId, callType: 'audio' });
      }
      
      // Check if user is already on the call
      const iAmOnCall = isOnCallRef.current || isCallActiveRef.current;
      
      // If already on call, auto-answer without showing popup
      if (iAmOnCall) {
        console.log('[VideoCall] ✅ Already on call - auto-answering incoming audio call');
        answerCall(call);
        return;
      }
      
      // If group call is active but I haven't joined, auto-answer
      if (groupCallActiveRef.current) {
        console.log('[VideoCall] Group call active - auto-answering incoming audio call');
        answerCall(call);
        return;
      }
      
      // Otherwise show popup
      console.log('[VideoCall] Setting pending AUDIO call');
      setPendingCall({ call, peerId, callerName, callType: 'audio' });
    };

    // Signal-on-Join: Auto-call when new user joins room (Socket.IO event)
    const handleUserConnected = (userId) => {
      console.log('[VideoCall] 🆕 New user joined room:', userId);
      console.log('[VideoCall] Current call state:', {
        isCallActive: isCallActiveRef.current,
        hasLocalStream: !!localStreamRef.current,
        isMe: userId === currentUser?.peerId,
        videoEnabled: videoEnabledRef.current,
        audioEnabled: audioEnabledRef.current
      });

      // IMPORTANT: Do NOT auto-call newly joined users.
      // Users should only receive media calls after they explicitly join the group call.
      const newUser = users?.find(u => u.peerId === userId);
      const newUserName = newUser?.peerName || newUser?.userName || newUser?.initial || 'User';
      setPeerMetadata(prev => ({ ...prev, [userId]: { userName: newUserName } }));
    };

    // Auto-reconnect calls when peer data channel re-establishes
    const handlePeerReconnected = ({ peerId: reconnectedPeerId }) => {
      // Clear disconnected state — peer is back
      setDisconnectedPeers(prev => {
        if (!prev[reconnectedPeerId]) return prev;
        const updated = { ...prev };
        delete updated[reconnectedPeerId];
        return updated;
      });
      // If we're on a call and had a call with this peer that's no longer active, re-call
      if (localStreamRef.current && (isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) {
        const existingCall = mediaCallsRef.current[reconnectedPeerId];
        const hasRemoteStream = !!remoteStreams[reconnectedPeerId];
        if (!existingCall || !hasRemoteStream) {
          console.log('[VideoCall] Peer reconnected, re-establishing call to:', reconnectedPeerId);
          if (existingCall) {
            try { existingCall.close(); } catch(_) {}
            delete mediaCallsRef.current[reconnectedPeerId];
          }
          // Small delay to let data channel stabilize
          setTimeout(() => {
            if (localStreamRef.current) {
              try {
                callPeer(reconnectedPeerId, localStreamRef.current, callPeerTypesRef.current[reconnectedPeerId] || 'video');
              } catch(e) {
                console.error('[VideoCall] Auto re-call failed:', e);
              }
            }
          }, 1000);
        }
      }
      // Also flush any calls that were queued while the peer transport was down
      flushPendingCalls(reconnectedPeerId);
    };

    // Full peer re-init: rejoin calls after our own peer was destroyed and recreated
    const handlePeerReinitialized = () => {
      console.log('[VideoCall] 🔄 Peer re-initialized — rejoining active calls');
      // Clear stale call references (old MediaConnection objects are dead)
      mediaCallsRef.current = {};
      // Clear stale stream references
      setRemoteStreams({});
      // Clear all call-related timers
      Object.values(callIceDisconnectTimersRef.current).forEach(t => clearTimeout(t));
      callIceDisconnectTimersRef.current = {};
      Object.values(callCloseRetryTimersRef.current).forEach(t => clearTimeout(t));
      callCloseRetryTimersRef.current = {};
      // If we were on a call, re-dial all participants after a short delay
      if (localStreamRef.current && (isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) {
        const callType = groupCallTypeRef.current || 'video';
        setTimeout(() => {
          if (!localStreamRef.current) return;
          console.log('[VideoCall] 🔄 Re-dialing all call participants after peer re-init');
          (usersRef.current || []).forEach(u => {
            if (u?.peerId && u.peerId !== currentUserRef.current?.peerId && u.status === 'active') {
              const peerCallType = callPeerTypesRef.current[u.peerId] || callType;
              try {
                callPeer(u.peerId, localStreamRef.current, peerCallType);
              } catch(e) {
                console.error('[VideoCall] Re-dial failed for', u.peerId, e);
              }
            }
          });
        }, 2000); // Wait for data channels to re-establish
      }
    };

    // Track call-state-update from peers to detect when all users have left the call
    const handleCallStateUpdate = ({ peerId, data }) => {
      if (data.videoCall === true || data.audioCall === true) {
        // Peer is on a call — track them and clear any disconnected state
        peersOnCallRef.current[peerId] = true;
        setDisconnectedPeers(prev => {
          if (!prev[peerId]) return prev;
          const updated = { ...prev };
          delete updated[peerId];
          return updated;
        });
      }
      if (data.videoCall === false && data.audioCall === false) {
        // This peer left the call — remove from tracking and mark as disconnected
        delete peersOnCallRef.current[peerId];
        setDisconnectedPeers(prev => ({ ...prev, [peerId]: true }));
        
        // Check if any other peers are still on a call (excluding ourselves and the peer who just left)
        const stillOnCall = Object.keys(peersOnCallRef.current).some(pid =>
          pid !== currentUser?.peerId && pid !== peerId
        );
        
        // If I'm not on a call either, and no one else is, hide join button and reset group call
        const imOnCall = isCallActiveRef.current || isOnCallRef.current;
        if (!imOnCall && !stillOnCall) {
          console.log('[VideoCall] All users have left the call - hiding join button, showing original call buttons');
          setGroupCallActive(false);
          setGroupCallType(null);
          groupCallTypeRef.current = null;
          setGroupCallInitiator(null);
          setShowJoinButton(false);
          groupCallActiveRef.current = false;
          groupCallInitiatorRef.current = null;
        }
      }
    };

    const unsubCallState = connectionManager.on('call-state-update', handleCallStateUpdate);

    // Subscribe to ConnectionManager events
    const unsubVideo = connectionManager.on('call:incoming:video', handleIncomingVideoCall);
    const unsubAudio = connectionManager.on('call:incoming:audio', handleIncomingAudioCall);
    const unsubUserConnected = connectionManager.on('user-connected', handleUserConnected);
    const unsubPeerConnected = connectionManager.on('peer:connected', handlePeerReconnected);
    const unsubPeerReinitialized = connectionManager.on('peer:reinitialized', handlePeerReinitialized);
    
    console.log('[VideoCall] ✅ Subscribed to peer call events using Signal-on-Join pattern');

    // Cleanup - unsubscribe from events
    return () => {
      console.log('[VideoCall] 🧹 Cleaning up peer call listeners');
      if (unsubVideo) unsubVideo();
      if (unsubAudio) unsubAudio();
      if (unsubUserConnected) unsubUserConnected();
      if (unsubPeerConnected) unsubPeerConnected();
      if (unsubPeerReinitialized) unsubPeerReinitialized();
      if (unsubCallState) unsubCallState();
      
      // Clean up socket listeners
      if (socket) {
        console.log('🟢 [VideoCall] [Socket Listeners] 🧹 Removing group call listeners');
        socket.off('group-call:started', handleGroupCallStart);
        socket.off('group-call:ended', handleGroupCallEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionManager, currentUser]);

  // Attach streams to video elements when they're rendered
  useEffect(() => {
    console.log('[VideoCall] useEffect triggered - localStream:', !!localStream, 'remoteStreams:', Object.keys(remoteStreams).length);
    
    // Attach local stream only when it changes
    if (localVideoRef.current) {
      const key = 'local';
      const last = lastAssignedStreamRef.current[key];
      if (last !== localStream) {
        lastAssignedStreamRef.current[key] = localStream;
        localVideoRef.current.srcObject = localStream || null;
        // Explicitly mute local video element to prevent echo
        localVideoRef.current.muted = true;
        localVideoRef.current.volume = 0;
        if (localStream) {
          localVideoRef.current.play?.().catch(() => {});
        }
      }
    }

    // Attach remote streams only when they change
    Object.entries(remoteStreams).forEach(([peerId, stream]) => {
      const videoElement = remoteVideoRefs.current[peerId];
      if (!videoElement) return;

      const last = lastAssignedStreamRef.current[peerId];
      if (last === stream) return;

      lastAssignedStreamRef.current[peerId] = stream;
      videoElement.srcObject = stream || null;
      // Ensure remote audio is not muted
      videoElement.muted = false;
      videoElement.volume = 1;
      if (stream) {
        videoElement.play?.().catch(() => {});
      }
    });
  }, [localStream, remoteStreams]);

  // --- Virtual background helpers ---
  const stopBackground = useCallback(() => {
    if (bgProcessorRef.current?.stop) {
      try { bgProcessorRef.current.stop(); } catch (_) {}
    }
    bgProcessorRef.current = null;
  }, []);

  const maybeApplyBackground = useCallback(async (rawStream) => {
    rawCameraStreamRef.current = rawStream;
    console.log('[VideoCall] 🎨 maybeApplyBackground called, bgSettingsRef.current:', bgSettingsRef.current);
    if (!bgSettingsRef.current?.enabled) {
      stopBackground();
      return rawStream;
    }
    stopBackground();
    const bg = await addBg(rawStream, bgSettingsRef.current);
    bgProcessorRef.current = bg;
    return bg.stream;
  }, [stopBackground]);

  const replaceTrackOnAllCalls = useCallback((newVideoTrack) => {
    try {
      Object.values(mediaCallsRef.current).forEach(call => {
        const pc = call?.peerConnection;
        if (!pc) return;
        const senders = pc.getSenders?.() || [];
        senders.forEach(sender => {
          if (sender.track?.kind === 'video' && newVideoTrack) {
            sender.replaceTrack(newVideoTrack).catch((e) => {
              console.warn('[VideoCall] BG replaceTrack failed:', e);
            });
          }
        });
      });
    } catch (_) {}
  }, []);

  const applyBackgroundLive = useCallback(async (settings) => {
    const rawStream = rawCameraStreamRef.current;
    if (!rawStream) return;
    const oldProcessor = bgProcessorRef.current;
    let outStream = rawStream;
    if (settings?.enabled) {
      const bg = await addBg(rawStream, settings);
      bgProcessorRef.current = bg;
      outStream = bg.stream;
    } else {
      bgProcessorRef.current = null;
    }

    // Swap the sender's track BEFORE tearing down the old processor so the
    // remote peer never receives a frozen frame from a removed canvas.
    const videoTrack = outStream.getVideoTracks()[0] || null;
    replaceTrackOnAllCalls(videoTrack);

    if (oldProcessor?.stop) {
      try { oldProcessor.stop(); } catch (_) {}
    }

    setLocalStream(outStream);
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = outStream;
      localVideoRef.current.play?.().catch(() => {});
    }
  }, [replaceTrackOnAllCalls]);

  const startVideoCall = async () => {
    console.log('[VideoCall] 🎬 Starting GROUP video call...');
    // CRITICAL: Reset intentionalCloseRef so auto-recovery works for the new call session
    intentionalCloseRef.current = false;
    try {
      console.log('[VideoCall] Requesting camera/mic permissions...');
      let stream = await navigator.mediaDevices.getUserMedia({
        video: VIDEO_CONSTRAINTS,
        audio: AUDIO_CONSTRAINTS
      });

      stream = await maybeApplyBackground(stream);
      console.log('[VideoCall] ✅ Got media stream:', {
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length
      });

      // Update local state
      setLocalStream(stream);
      setVideoEnabled(true);
      setAudioEnabled(true);
      setIsCallActive(true);
      setIsOnCall(true);
      
      // CRITICAL: Update refs immediately
      localStreamRef.current = stream;
      videoEnabledRef.current = true;
      audioEnabledRef.current = true;
      isCallActiveRef.current = true;
      groupCallActiveRef.current = true;
      groupCallInitiatorRef.current = currentUserRef.current?.peerId;
      isOnCallRef.current = true;
      // Track self as on-call
      if (currentUserRef.current?.peerId) {
        peersOnCallRef.current[currentUserRef.current.peerId] = true;
      }
      
      // Attach local stream to video element
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // CRITICAL: Update server state immediately so others see video
      if (connectionManager && currentUser) {
        const apiBase = process.env.REACT_APP_API_BASE || '';
        await fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            peerId: currentUser.peerId,
            video: { status: true, enabled: true, muted: false }
          })
        }).catch(e => console.error('[VideoCall] Failed to update server state:', e));
        console.log('[VideoCall] ✅ Server state updated - video ENABLED on call start');
      }

      // Broadcast to ALL users that a group call started
      console.log('[VideoCall] 📞 Broadcasting group call start to all users');
      console.log('[VideoCall] Current user:', currentUserRef.current);
      console.log('[VideoCall] Room ID:', connectionManager?.roomId);
      broadcastGroupCallStart.current('video');
      
      // Set group call state
      setGroupCallActive(true);
      setGroupCallType('video');
      groupCallTypeRef.current = 'video';
      setGroupCallInitiator(currentUser?.peerId);
      
      // Add call event to chat
      if (onCallEvent) {
        onCallEvent('Group video call started', 'fa-video');
      }
      
      showToast('Group video call started - waiting for others to join', 'success');
    } catch (error) {
      console.error('Error starting video call:', error);
      showToast('Failed to start video call', 'error');
    }
  };

  const startAudioCall = async () => {
    console.log('[VideoCall] 🎬 Starting GROUP audio call...');
    // CRITICAL: Reset intentionalCloseRef so auto-recovery works for the new call session
    intentionalCloseRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS
      });

      setLocalStream(stream);
      setAudioEnabled(true);
      setIsCallActive(true);
      setIsOnCall(true);
      
      localStreamRef.current = stream;
      audioEnabledRef.current = true;
      isCallActiveRef.current = true;
      groupCallActiveRef.current = true;
      groupCallInitiatorRef.current = currentUserRef.current?.peerId;
      isOnCallRef.current = true;
      // Track self as on-call
      if (currentUserRef.current?.peerId) {
        peersOnCallRef.current[currentUserRef.current.peerId] = true;
      }

      // Setup audio context for gain control
      setupAudioContext(stream);

      // Broadcast to ALL users that a group call started
      console.log('[VideoCall] 📞 Broadcasting group call start to all users');
      console.log('[VideoCall] Current user:', currentUserRef.current);
      console.log('[VideoCall] Room ID:', connectionManager?.roomId);
      broadcastGroupCallStart.current('audio');
      
      // Set group call state
      setGroupCallActive(true);
      setGroupCallType('audio');
      groupCallTypeRef.current = 'audio';
      setGroupCallInitiator(currentUser?.peerId);

      // Add call event to chat
      if (onCallEvent) {
        onCallEvent('Group audio call started', 'fa-phone');
      }

      showToast('Group audio call started - waiting for others to join', 'success');
    } catch (error) {
      console.error('Error starting audio call:', error);
      showToast('Failed to start audio call', 'error');
    }
  };

  // ICE failure recovery helper for calls
  const setupCallIceMonitoring = (call, peerId, callType) => {
    if (!call || !call.peerConnection) return;
    callPeerTypesRef.current[peerId] = callType;
    
    call.peerConnection.oniceconnectionstatechange = () => {
      const iceState = call.peerConnection?.iceConnectionState;
      console.log('[VideoCall] ICE state:', iceState, 'for peer:', peerId);
      
      if (iceState === 'disconnected') {
        // Grace period - ICE may recover
        if (callIceDisconnectTimersRef.current[peerId]) {
          clearTimeout(callIceDisconnectTimersRef.current[peerId]);
        }
        callIceDisconnectTimersRef.current[peerId] = setTimeout(() => {
          console.warn('[VideoCall] ICE disconnected for 10s, attempting re-call to:', peerId);
          // Clean up old call
          try { call.close(); } catch(_) {}
          delete mediaCallsRef.current[peerId];
          setRemoteStreams(prev => {
            const updated = { ...prev };
            delete updated[peerId];
            return updated;
          });
          // Re-call if we still have a local stream
          if (localStreamRef.current && !connectionManager?.getPeer()?.destroyed) {
            console.log('[VideoCall] Re-calling peer after ICE failure:', peerId);
            callPeer(peerId, localStreamRef.current, callPeerTypesRef.current[peerId] || 'video');
          }
        }, 10000);
      } else if (iceState === 'failed') {
        // Immediate recovery
        console.error('[VideoCall] ICE failed, attempting immediate re-call to:', peerId);
        if (callIceDisconnectTimersRef.current[peerId]) {
          clearTimeout(callIceDisconnectTimersRef.current[peerId]);
          delete callIceDisconnectTimersRef.current[peerId];
        }
        try { call.close(); } catch(_) {}
        delete mediaCallsRef.current[peerId];
        setRemoteStreams(prev => {
          const updated = { ...prev };
          delete updated[peerId];
          return updated;
        });
        if (localStreamRef.current && !connectionManager?.getPeer()?.destroyed) {
          setTimeout(() => {
            console.log('[VideoCall] Re-calling peer after ICE failure:', peerId);
            callPeer(peerId, localStreamRef.current, callPeerTypesRef.current[peerId] || 'video');
          }, 1000);
        }
      } else if (iceState === 'connected' || iceState === 'completed') {
        // ICE recovered
        if (callIceDisconnectTimersRef.current[peerId]) {
          clearTimeout(callIceDisconnectTimersRef.current[peerId]);
          delete callIceDisconnectTimersRef.current[peerId];
        }
      }
    };
  };

  // Re-call a peer after an unexpected media-call close (connection issue, not user action)
  const scheduleCallCloseRecovery = (peerId) => {
    // Skip if the user ended the call themselves
    if (intentionalCloseRef.current) return;
    // Skip if we're no longer on a call
    if (!localStreamRef.current || !(isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) return;
    if (callCloseRetryTimersRef.current[peerId]) return; // Already scheduled

    callCloseRetryTimersRef.current[peerId] = setTimeout(() => {
      delete callCloseRetryTimersRef.current[peerId];
      // Re-check conditions after the grace period:
      // - peer intentionally left (call-state-update marked them disconnected)
      // - a new call was already re-established
      // - we ended the call in the meantime
      if (intentionalCloseRef.current) return;
      if (disconnectedPeersRef.current[peerId]) {
        console.log('[VideoCall] Peer', peerId, 'intentionally left - not re-calling');
        return;
      }
      if (mediaCallsRef.current[peerId]) return;
      if (!localStreamRef.current || !(isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) return;
      console.log('[VideoCall] 🔄 Unexpected call close - auto re-calling:', peerId);
      try {
        callPeer(peerId, localStreamRef.current, callPeerTypesRef.current[peerId] || 'video');
      } catch (e) {
        console.error('[VideoCall] Auto re-call after close failed:', e);
      }
    }, 3000); // 3s grace: lets an intentional-leave call-state-update arrive first
  };

  // Flush calls that were queued while the peer transport was down
  const flushPendingCalls = (onlyPeerId = null) => {
    const entries = Object.entries(pendingCallsRef.current);
    if (entries.length === 0) return;
    if (!localStreamRef.current || !(isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) {
      pendingCallsRef.current = {};
      return;
    }
    entries.forEach(([peerId, callType]) => {
      if (onlyPeerId && peerId !== onlyPeerId) return;
      delete pendingCallsRef.current[peerId];
      if (mediaCallsRef.current[peerId]) return;
      console.log('[VideoCall] 🔄 Flushing queued call to:', peerId, '(type:', callType, ')');
      try {
        callPeer(peerId, localStreamRef.current, callType || 'video');
      } catch (e) {
        console.error('[VideoCall] Queued call flush failed:', e);
      }
    });
  };

  const callPeer = (peerId, stream, callType = 'video') => {
    console.log('[VideoCall] 📞 callPeer() called:', {
      peerId,
      callType,
      hasStream: !!stream,
      streamTracks: stream ? {
        video: stream.getVideoTracks().length,
        audio: stream.getAudioTracks().length
      } : null,
      currentUser: currentUser?.userName || currentUser?.peerId
    });
    
    const peer = connectionManager.getPeer();
    console.log('[VideoCall] Got peer instance:', {
      hasPeer: !!peer,
      peerId: peer?.id,
      destroyed: peer?.destroyed,
      disconnected: peer?.disconnected
    });
    
    if (!peer || peer.destroyed || peer.disconnected) {
      // Peer transport is down — queue the call; it will be flushed on
      // peer:connected / peer:reinitialized instead of being dropped
      console.warn('[VideoCall] ⚠️ Peer transport not ready (destroyed:', peer?.destroyed, 'disconnected:', peer?.disconnected, ') - queueing call to', peerId);
      pendingCallsRef.current[peerId] = callType;
      callPeerTypesRef.current[peerId] = callType;
      return;
    }

    // Skip if already have an active call with this peer (prevents duplicate streams and echo)
    if (mediaCallsRef.current[peerId]) {
      console.log('[VideoCall] Already have active call with peer', peerId, '- skipping duplicate call to prevent echo');
      return;
    }

    // A real call is going out — clear queued/intentional-close state
    delete pendingCallsRef.current[peerId];
    intentionalCloseRef.current = false;
    if (callCloseRetryTimersRef.current[peerId]) {
      clearTimeout(callCloseRetryTimersRef.current[peerId]);
      delete callCloseRetryTimersRef.current[peerId];
    }

    // Store callee's name with peerName prioritized over server-assigned userName
    const callee = users?.find(u => u.peerId === peerId);
    const calleeName = callee?.peerName || callee?.userName || callee?.initial || peerId.slice(0, 8);
    console.log('[VideoCall] Storing callee metadata for outgoing call:', { peerId, userName: calleeName });
    setPeerMetadata(prev => ({ ...prev, [peerId]: { userName: calleeName } }));
    
    // Prepare comprehensive metadata for the call
    const myMetadata = {
      callType,
      from: currentUser,
      peerName: currentUser?.peerName,
      userName: currentUser?.peerName || currentUser?.userName || currentUser?.initial || 'Unknown',
      initial: currentUser?.initial,
      peerId: currentUser?.peerId
    };
    
    console.log('[VideoCall] Making PeerJS call to:', peerId, 'with callType:', callType, 'metadata:', myMetadata);
    const call = peer.call(peerId, stream, { metadata: myMetadata });
    
    console.log('[VideoCall] ✅ Call object created:', {
      callId: call?.connectionId,
      peer: call?.peer,
      open: call?.open,
      type: call?.type
    });

    mediaCallsRef.current[peerId] = call;
    
    // Start monitoring WebRTC stats for this call
    startStatsMonitoring(call, peerId);

    // Timeout: if no stream received within 15s, close and trigger recovery
    let streamReceived = false;
    const noStreamTimeout = setTimeout(() => {
      if (!streamReceived && mediaCallsRef.current[peerId] === call) {
        console.warn('[VideoCall] ⚠️ No stream received from', peerId, 'within 15s — closing and retrying');
        try { call.close(); } catch(_) {}
        delete mediaCallsRef.current[peerId];
        scheduleCallCloseRecovery(peerId);
      }
    }, 15000);

    call.on('stream', (remoteStream) => {
      streamReceived = true;
      clearTimeout(noStreamTimeout);
      console.log('[VideoCall] Received remote stream from:', peerId);
      setRemoteStreams(prev => ({ ...prev, [peerId]: remoteStream }));
      // Peer is back — clear disconnected state
      setDisconnectedPeers(prev => {
        if (!prev[peerId]) return prev;
        const updated = { ...prev };
        delete updated[peerId];
        return updated;
      });
      
      // Initialize last frame time
      lastFrameTimeRef.current[peerId] = Date.now();
      console.log('[VideoCall] Initialized frame tracking for:', peerId);
      
      // Attach to video element with retry + play() + frame tracking
      attachRemoteStream(peerId, remoteStream, call);
    });

    call.on('close', () => {
      console.log('[VideoCall] Call closed with:', peerId);
      clearTimeout(noStreamTimeout);
      
      // Clean up stats for this peer
      if (lastStatsRef.current[peerId]) {
        delete lastStatsRef.current[peerId];
        console.log('[VideoCall] Cleaned up stats for closed call:', peerId);
      }
      if (statsIntervalRef.current?.[peerId]) {
        clearInterval(statsIntervalRef.current[peerId]);
        delete statsIntervalRef.current[peerId];
      }
      
      handlePeerStreamRemoved(peerId);
      scheduleCallCloseRecovery(peerId);
    });

    call.on('error', (err) => {
      console.error('[VideoCall] ❌ Call error with peer', peerId, err);
      showToast(`Call error: ${err.type || 'Unknown error'}`, 'error');
    });
    
    // ICE state monitoring with auto-recovery
    setupCallIceMonitoring(call, peerId, callType);
  };

  const answerCall = async (call) => {
    if (!call || typeof call.answer !== 'function') {
      console.warn('[VideoCall] ⚠️ Cannot answer call - invalid call object:', call);
      return;
    }

    console.log('[VideoCall] 📞 Answering call from:', call.peer, 'callType:', call.metadata?.callType);
    console.log('[VideoCall] Current state:', {
      hasLocalStream: !!localStream,
      isCallActive,
      videoEnabled,
      audioEnabled,
      isVisible
    });
    
    // ONE-WAY BROADCAST: Answer in receive-only mode
    // Users must manually start their video to be visible to others
    // CRITICAL: Use ref to avoid stale closure
    let streamToAnswer = localStreamRef.current;
    
    // Only send our stream if we already have one active
    // Don't auto-start camera/mic for incoming calls
    console.log('[VideoCall] Answering in receive-only mode (use "Start Video" button to be visible)');

    // Answer with the stream (or without if we don't have one)
    console.log('[VideoCall] Answering call with stream:', !!streamToAnswer);
    try {
      if (streamToAnswer) {
        call.answer(streamToAnswer);
      } else {
        // PeerJS can throw if answer() is called without a stream in some states.
        // Use an empty MediaStream to ensure startConnection is initialized.
        call.answer(new MediaStream());
      }
    } catch (e) {
      console.error('[VideoCall] ❌ Failed to answer call:', e);
      return;
    }
    mediaCallsRef.current[call.peer] = call;
    console.log('[VideoCall] ✅ Call answered');
    
    // Start monitoring WebRTC stats for this call
    startStatsMonitoring(call, call.peer);
    
    // Ensure call is active so UI shows
    if (!isCallActiveRef.current) {
      console.log('[VideoCall] Setting isCallActive=true');
      setIsCallActive(true);
      isCallActiveRef.current = true;
    }
    if (!isOnCallRef.current) {
      console.log('[VideoCall] Setting isOnCall=true');
      setIsOnCall(true);
      isOnCallRef.current = true;
    }

    call.on('stream', (remoteStream) => {
      console.log('[VideoCall] Received remote stream from:', call.peer);
      console.log('[VideoCall] Remote stream details:', {
        peerId: call.peer,
        videoTracks: remoteStream.getVideoTracks().length,
        audioTracks: remoteStream.getAudioTracks().length,
        active: remoteStream.active
      });
      
      setRemoteStreams(prev => {
        const updated = { ...prev, [call.peer]: remoteStream };
        console.log('[VideoCall] Remote streams count:', Object.keys(updated).length);
        return updated;
      });
      // Initialize frame tracking for answered call
      lastFrameTimeRef.current[call.peer] = Date.now();
      // Peer is back — clear disconnected state
      setDisconnectedPeers(prev => {
        if (!prev[call.peer]) return prev;
        const updated = { ...prev };
        delete updated[call.peer];
        return updated;
      });
      
      // Attach to video element with retry + play() + frame tracking
      attachRemoteStream(call.peer, remoteStream, call);
    });

    call.on('close', () => {
      handlePeerStreamRemoved(call.peer);
      scheduleCallCloseRecovery(call.peer);
    });
    
    // ICE state monitoring with auto-recovery for incoming calls
    setupCallIceMonitoring(call, call.peer, call.metadata?.callType || 'video');
  };

  const acceptPendingCall = async () => {
    if (!pendingCall) return;
    
    // CRITICAL: Reset intentionalCloseRef so auto-recovery works for the new call
    intentionalCloseRef.current = false;
    console.log('[VideoCall] User accepted group call');
    
    // For group calls, start my own media and call all participants
    if (pendingCall.isGroupCall) {
      // CRITICAL: Set state IMMEDIATELY before async operations
      // This prevents popup from reappearing if component remounts during getUserMedia
      setIsOnCall(true);
      setIsCallActive(true);
      isOnCallRef.current = true;
      isCallActiveRef.current = true;
      setPendingCall(null); // Clear popup immediately
      
      try {
        const callType = pendingCall.callType;
        let stream;
        
        if (callType === 'video') {
          stream = await navigator.mediaDevices.getUserMedia({
            video: VIDEO_CONSTRAINTS,
            audio: AUDIO_CONSTRAINTS
          });
          stream = await maybeApplyBackground(stream);
          setVideoEnabled(true);
        } else {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: AUDIO_CONSTRAINTS
          });
          setupAudioContext(stream);
        }
        
        setLocalStream(stream);
        setAudioEnabled(true);
        
        localStreamRef.current = stream;
        videoEnabledRef.current = callType === 'video';
        audioEnabledRef.current = true;
        groupCallActiveRef.current = true;
        groupCallInitiatorRef.current = pendingCall.initiatorId;
        // Track self as on-call
        if (currentUser?.peerId) {
          peersOnCallRef.current[currentUser.peerId] = true;
        }
        // Tell the server we joined the active group call, so its participants
        // list correctly reflects everyone actually on the call (not just the initiator)
        if (connectionManager?.socket && currentUser?.peerId) {
          connectionManager.socket.emit('group-call:join', {
            roomId: connectionManager.roomId,
            peerId: currentUser.peerId
          });
        }
        
        if (localVideoRef.current && callType === 'video') {
          localVideoRef.current.srcObject = stream;
        }
        
        // CRITICAL: Update server state so others see our video
        if (connectionManager && currentUser && callType === 'video') {
          try {
            const apiBase = process.env.REACT_APP_API_BASE || '';
            await fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                peerId: currentUser.peerId,
                video: { status: true, enabled: true, muted: false }
              })
            });
            console.log('[VideoCall] ✅ Server state updated - video ENABLED on accept');
          } catch (e) {
            console.error('[VideoCall] Failed to update server state:', e);
          }
        }
        
        // Call all connected peers who are on the call
        const connections = connectionManager.getConnections();
        console.log('[VideoCall] Joining group call - calling all peers:', Object.keys(connections));
        Object.keys(connections).forEach(peerId => {
          if (peerId !== currentUser?.peerId) {
            console.log('[VideoCall] 📞 Calling peer in group:', peerId);
            callPeer(peerId, stream, callType);
          }
        });
        
        setShowJoinButton(false);
        showToast(`Joined group ${callType} call`, 'success');
        
      } catch (error) {
        console.error('Error joining group call:', error);
        showToast('Failed to join call', 'error');
        // Reset state on error
        setIsOnCall(false);
        setIsCallActive(false);
        isOnCallRef.current = false;
        isCallActiveRef.current = false;
      }
    } else {
      // Old one-on-one call
      const icon = pendingCall.callType === 'video' ? '📹' : '🎙️';
      showToast(`${icon} Incoming ${pendingCall.callType} call from ${pendingCall.callerName}`, 'info');
      await answerCall(pendingCall.call);
      setPendingCall(null);
    }
  };

  const declinePendingCall = () => {
    if (!pendingCall) return;
    
    if (pendingCall.isGroupCall) {
      // Mark this group call as rejected so we don't show popup again
      const callId = `${pendingCall.initiatorId}_${pendingCall.callType}`;
      setRejectedGroupCallId(callId);
      console.log('[VideoCall] 🚫 User rejected group call:', callId);
      
      // Keep group call state active so join button remains visible
      setShowJoinButton(true);
      setGroupCallActive(true);
      setGroupCallType(pendingCall.callType);
      groupCallTypeRef.current = pendingCall.callType;
      setGroupCallInitiator(pendingCall.initiatorId);
      groupCallActiveRef.current = true;
      groupCallInitiatorRef.current = pendingCall.initiatorId;
      
      setPendingCall(null);
      showToast('You can join the call later from the call button', 'info');
    } else {
      // Old one-on-one call logic
      if (pendingCall.call) {
        pendingCall.call.close();
      }
      setPendingCall(null);
      showToast('Call declined', 'info');
    }
  };

  const joinActiveGroupCall = async () => {
    if (!connectionManager) return;
    // CRITICAL: Reset intentionalCloseRef so auto-recovery works after rejoining
    intentionalCloseRef.current = false;
    if (!groupCallActive || !groupCallType) {
      showToast('No active call to join', 'info');
      return;
    }
    if (isOnCall) return;

    try {
      const callType = groupCallType;
      let stream;

      if (callType === 'video') {
        stream = await navigator.mediaDevices.getUserMedia({
          video: VIDEO_CONSTRAINTS,
          audio: AUDIO_CONSTRAINTS
        });
        stream = await maybeApplyBackground(stream);
        setVideoEnabled(true);
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: AUDIO_CONSTRAINTS
        });
        setupAudioContext(stream);
        setVideoEnabled(false);
      }

      setLocalStream(stream);
      setAudioEnabled(true);
      setIsCallActive(true);
      setIsOnCall(true);
      
      localStreamRef.current = stream;
      videoEnabledRef.current = callType === 'video';
      audioEnabledRef.current = true;
      isCallActiveRef.current = true;
      isOnCallRef.current = true;
      groupCallActiveRef.current = true;
      // Track self as on-call
      if (currentUser?.peerId) {
        peersOnCallRef.current[currentUser.peerId] = true;
      }
      // Tell the server we joined the active group call, so its participants
      // list correctly reflects everyone actually on the call (not just the initiator)
      if (connectionManager?.socket && currentUser?.peerId) {
        connectionManager.socket.emit('group-call:join', {
          roomId: connectionManager.roomId,
          peerId: currentUser.peerId
        });
      }
      groupCallInitiatorRef.current = groupCallInitiator || groupCallInitiatorRef.current;
      
      if (localVideoRef.current && callType === 'video') {
        localVideoRef.current.srcObject = stream;
      }
      
      // Update server state so others see our video
      if (connectionManager && currentUser && callType === 'video') {
        const apiBase = process.env.REACT_APP_API_BASE || '';
        await fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            peerId: currentUser.peerId,
            video: { status: true, enabled: true, muted: false }
          })
        }).catch(e => console.error('[VideoCall] Failed to update server state:', e));
        console.log('[VideoCall] ✅ Server state updated - video ENABLED on rejoin');
      }
      
      const connections = connectionManager.getConnections();
      console.log('[VideoCall] Joining active group call - calling all peers:', Object.keys(connections));
      Object.keys(connections).forEach(peerId => {
        if (peerId !== currentUser?.peerId) {
          console.log('[VideoCall] 📞 Calling peer in group:', peerId);
          callPeer(peerId, stream, callType);
        }
      });
      

      setShowJoinButton(false);
      showToast(`Joined group ${callType} call`, 'success');
    } catch (error) {
      console.error('Error joining active group call:', error);
      showToast('Failed to join call', 'error');
    }
  };

  const setupAudioContext = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)(); // eslint-disable-line new-parens
      const source = audioContext.createMediaStreamSource(stream);
      const gainNode = audioContext.createGain();
      const analyser = audioContext.createAnalyser();
      
      source.connect(gainNode);
      gainNode.connect(analyser);
      analyser.connect(audioContext.destination);
      
      audioContextRef.current = audioContext;
      gainNodeRef.current = gainNode;
      analyserRef.current = analyser;
      
      // Set initial gain
      gainNode.gain.value = micGain / 100;
    } catch (error) {
      console.error('Error setting up audio context:', error);
    }
  };

  const handleMicGainChange = (value) => { // eslint-disable-line no-unused-vars
    setMicGain(value);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = value / 100;
    }
  };

  useImperativeHandle(ref, () => ({
    startVideoCall,
    startAudioCall,
    joinActiveGroupCall,
    toggleVideo,
    toggleAudio,
    endCall: stopAllCalls,
    enterPIP: tryEnterPIP,
    exitPIP: tryExitPIP,
    getState: () => ({
      isCallActive,
      isOnCall,
      groupCallActive,
      groupCallType,
      videoEnabled,
      audioEnabled,
      hasLocalStream: !!localStream
    }),
    getBandwidth: () => totalBandwidth
  }));

  const toggleVideo = async () => {
    if (!localStream) return;

    const getActiveVideoSender = (call) => {
      try {
        const pc = call?.peerConnection;
        if (!pc) return null;
        const senders = pc.getSenders ? pc.getSenders() : [];
        return senders.find(s => s && s.track && s.track.kind === 'video') || null;
      } catch (_) {
        return null;
      }
    };

    const replaceOutboundVideoTrack = async (newTrackOrNull) => {
      const calls = Object.values(mediaCallsRef.current || {});
      await Promise.all(
        calls.map(async (call) => {
          const sender = getActiveVideoSender(call);
          if (!sender || typeof sender.replaceTrack !== 'function') return;
          try {
            await sender.replaceTrack(newTrackOrNull);
          } catch (e) {
            console.warn('[VideoCall] replaceTrack failed for peer:', call?.peer, e);
          }
        })
      );
    };

    const existing = localStream.getVideoTracks()[0];
    const canDisableByEnabledFlag = existing && existing.readyState === 'live';

    // If video is currently enabled => disable it.
    if (videoEnabled && canDisableByEnabledFlag) {
      console.log('[VideoCall] 🔴 DISABLING VIDEO - turning off camera');
      
      try {
        existing.enabled = false;
      } catch (_) {}

      setVideoEnabled(false);
      videoEnabledRef.current = false;

      // Don't replace track or clear srcObject - just disable the track
      // The showVideo logic will handle showing initials
      console.log('[VideoCall] Video track disabled (enabled=false), track remains in stream');

      // CRITICAL FIX: Update server state to sync icons across users
      // IMPORTANT: Also send audio status to keep audio enabled when video is off
      if (connectionManager && currentUser) {
        try {
          const apiBase = process.env.REACT_APP_API_BASE || '';
          const audioTrack = localStream.getAudioTracks()[0];
          const audioStatus = audioTrack ? audioTrack.enabled : audioEnabledRef.current;
          
          await fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              peerId: currentUser.peerId,
              video: { status: true, enabled: false, muted: false },
              audio: { status: true, muted: !audioStatus }
            })
          });
          console.log('[VideoCall] ✅ Server state updated - camera OFF, audio:', audioStatus ? 'ON' : 'OFF');
          
          // Broadcast to all users via Socket.IO
          connectionManager.socket?.emit('room-users', { 
            roomId: connectionManager.roomId 
          });
        } catch (e) {
          console.error('[VideoCall] Failed to update server state:', e);
        }
        
        connectionManager.broadcastData({
          type: 'call-state-update',
          data: {
            peerId: currentUser.peerId,
            videoCall: false,
            audioCall: !!audioEnabledRef.current,
            timestamp: Date.now()
          }
        });
      }
      
      return;
    }

    // Otherwise enable video. If track is missing or ended, reacquire a new one.
    try {
      const currentTrack = localStream.getVideoTracks()[0];
      const needsNewTrack = !currentTrack || currentTrack.readyState !== 'live';
      let newVideoTrack = currentTrack;

      if (needsNewTrack) {
        const newStream = await navigator.mediaDevices.getUserMedia({ video: VIDEO_CONSTRAINTS, audio: AUDIO_CONSTRAINTS });
        let outStream = newStream;
        if (bgSettings.enabled) outStream = await maybeApplyBackground(newStream);
        newVideoTrack = outStream.getVideoTracks()[0];

        // Remove old ended track if present
        if (currentTrack) {
          try {
            localStream.removeTrack(currentTrack);
          } catch (_) {}
        }

        // Add the new live track into our local stream
        if (newVideoTrack) {
          localStream.addTrack(newVideoTrack);
        }
      }

      if (newVideoTrack) {
        try {
          newVideoTrack.enabled = true;
          console.log('[VideoCall] 🟢 VIDEO ENABLED - camera is now ON');
        } catch (_) {}
      }

      setVideoEnabled(true);
      videoEnabledRef.current = true;

      // CRITICAL FIX: Force refresh local video preview
      if (localVideoRef.current) {
        // Clear and reassign to force browser to re-render
        localVideoRef.current.srcObject = null;
        setTimeout(() => {
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = localStream;
            localVideoRef.current.play?.().catch(() => {});
          }
        }, 50);
      } else {
        // If no video ref yet, just assign directly
        setTimeout(() => {
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = localStream;
          }
        }, 100);
      }

      // Propagate to peers - only if needed (new track acquired)
      if (needsNewTrack && newVideoTrack) {
        await replaceOutboundVideoTrack(newVideoTrack);
      }

      // CRITICAL FIX: Update server state to sync icons across users
      if (connectionManager && currentUser) {
        try {
          const apiBase = process.env.REACT_APP_API_BASE || '';
          await fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              peerId: currentUser.peerId,
              video: { status: true, enabled: true, muted: false }
            })
          });
          console.log('[VideoCall] ✅ Server state updated - camera ON');
          
          // Broadcast to all users via Socket.IO
          connectionManager.socket?.emit('room-users', { 
            roomId: connectionManager.roomId 
          });
        } catch (e) {
          console.error('[VideoCall] Failed to update server state:', e);
        }
        
        connectionManager.broadcastData({
          type: 'call-state-update',
          data: {
            peerId: currentUser.peerId,
            videoCall: true,
            audioCall: !!audioEnabledRef.current,
            timestamp: Date.now()
          }
        });
      }
    } catch (e) {
      console.error('[VideoCall] Failed to enable video:', e);
      showToast('Failed to start camera', 'error');
    }
  };

  const toggleAudio = async () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
        audioEnabledRef.current = audioTrack.enabled;
        
        // CRITICAL FIX: Update server state to sync icons across users
        if (connectionManager && currentUser) {
          try {
            const apiBase = process.env.REACT_APP_API_BASE || '';
            await fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                peerId: currentUser.peerId,
                audio: { status: true, muted: !audioTrack.enabled }
              })
            });
          } catch (e) {
            console.error('[VideoCall] Failed to update server state:', e);
          }
          
          connectionManager.broadcastData({
            type: 'call-state-update',
            data: {
              peerId: currentUser.peerId,
              videoCall: !!videoEnabledRef.current,
              audioCall: audioTrack.enabled,
              timestamp: Date.now()
            }
          });
        }
      }
    }
  };

  // WebRTC Stats Monitoring for Bandwidth Display
  const startStatsMonitoring = (call, peerId) => {
    if (!call?.peerConnection) {
      console.log('[VideoCall] No peerConnection for', peerId);
      return;
    }
    
    console.log('[VideoCall] Starting stats monitoring for:', peerId);
    
    // Initialize lastStats for this peer
    if (!lastStatsRef.current[peerId]) {
      lastStatsRef.current[peerId] = { 
        bytesSent: 0, 
        bytesReceived: 0, 
        videoBytesSent: 0, 
        videoBytesReceived: 0,
        audioBytesSent: 0,
        audioBytesReceived: 0,
        timestamp: Date.now() 
      };
    }
    
    const intervalId = setInterval(async () => {
      try {
        // Check if peerConnection still exists (call might have been closed)
        if (!call.peerConnection) {
          console.log('[VideoCall] PeerConnection is null, clearing stats interval for:', peerId);
          clearInterval(intervalId);
          if (statsIntervalRef.current?.[peerId]) {
            delete statsIntervalRef.current[peerId];
          }
          return;
        }
        
        const stats = await call.peerConnection.getStats();
        let bytesSent = 0, bytesReceived = 0;
        let videoBytesSent = 0, videoBytesReceived = 0;
        let audioBytesSent = 0, audioBytesReceived = 0;
        
        stats.forEach(report => {
          if (report.type === 'outbound-rtp') {
            if (report.bytesSent) bytesSent += report.bytesSent;
            if (report.kind === 'video' && report.bytesSent) videoBytesSent += report.bytesSent;
            if (report.kind === 'audio' && report.bytesSent) audioBytesSent += report.bytesSent;
          }
          if (report.type === 'inbound-rtp') {
            if (report.bytesReceived) bytesReceived += report.bytesReceived;
            if (report.kind === 'video' && report.bytesReceived) videoBytesReceived += report.bytesReceived;
            if (report.kind === 'audio' && report.bytesReceived) audioBytesReceived += report.bytesReceived;
          }
        });
        
        // console.log('[VideoCall] Raw stats for', peerId, ':', { bytesSent, bytesReceived });
        
        // Calculate bitrates (bits per second)
        const lastStats = lastStatsRef.current[peerId];
        
        // First reading or reset detection
        if (!lastStats || bytesSent < lastStats.bytesSent || bytesReceived < lastStats.bytesReceived) {
          console.log('[VideoCall] Initializing/resetting stats for', peerId, '- Counter reset detected');
          lastStatsRef.current[peerId] = { 
            bytesSent, 
            bytesReceived, 
            videoBytesSent, 
            videoBytesReceived,
            audioBytesSent,
            audioBytesReceived,
            timestamp: Date.now() 
          };
          return; // Skip this interval, wait for next reading
        }
        
        const timeDiff = (Date.now() - lastStats.timestamp) / 1000; // seconds
        
        // Require minimum time interval (0.8s) to avoid spikes and ensure stable readings
        if (timeDiff < 0.8 || timeDiff > 5) {
          console.log('[VideoCall] Skipping stats - timeDiff out of range:', timeDiff);
          return;
        }
        
        // Calculate deltas
        const bytesSentDelta = bytesSent - lastStats.bytesSent;
        const bytesReceivedDelta = bytesReceived - lastStats.bytesReceived;
        
        // Skip if deltas are invalid (should never be negative at this point)
        if (bytesSentDelta < 0 || bytesReceivedDelta < 0) {
          console.log('[VideoCall] Invalid deltas detected, resetting:', { bytesSentDelta, bytesReceivedDelta });
          lastStatsRef.current[peerId] = { 
            bytesSent, 
            bytesReceived, 
            videoBytesSent, 
            videoBytesReceived,
            audioBytesSent,
            audioBytesReceived,
            timestamp: Date.now() 
          };
          return;
        }
        
        // Calculate bitrates
        const bitrateSent = (bytesSentDelta * 8 / timeDiff / 1024).toFixed(1); // kbps
        const bitrateReceived = (bytesReceivedDelta * 8 / timeDiff / 1024).toFixed(1); // kbps
        
        // console.log('[VideoCall] Calculated bitrates for', peerId, ':', { bitrateSent, bitrateReceived, timeDiff });
        
        setStreamStats(prev => ({
            ...prev,
            [peerId]: {
              sent: bitrateSent,
              received: bitrateReceived,
              videoSent: ((videoBytesSent - (lastStats.videoBytesSent || 0)) * 8 / timeDiff / 1024).toFixed(1),
              videoReceived: ((videoBytesReceived - (lastStats.videoBytesReceived || 0)) * 8 / timeDiff / 1024).toFixed(1),
              audioSent: ((audioBytesSent - (lastStats.audioBytesSent || 0)) * 8 / timeDiff / 1024).toFixed(1),
              audioReceived: ((audioBytesReceived - (lastStats.audioBytesReceived || 0)) * 8 / timeDiff / 1024).toFixed(1)
            }
          }));
          
        // Update lastStats for next interval
        lastStatsRef.current[peerId] = { 
          bytesSent, 
          bytesReceived, 
          videoBytesSent, 
          videoBytesReceived,
          audioBytesSent,
          audioBytesReceived,
          timestamp: Date.now() 
        };
      } catch (err) {
        console.warn('[VideoCall] Error getting stats for', peerId, err);
        // If error persists, clear the interval to prevent spam
        if (err.message && err.message.includes('null')) {
          clearInterval(intervalId);
          if (statsIntervalRef.current?.[peerId]) {
            delete statsIntervalRef.current[peerId];
          }
        }
      }
    }, 1000); // Update every second
    
    // Store interval ID for cleanup
    if (!statsIntervalRef.current) statsIntervalRef.current = {};
    statsIntervalRef.current[peerId] = intervalId;
  };

  // Calculate total bandwidth from all connections
  useEffect(() => {
    const calculateTotal = () => {
      let totalSent = 0, totalReceived = 0;
      
      // Aggregate all sent/received from peer connections
      Object.values(streamStats).forEach(stat => {
        totalSent += parseFloat(stat.sent || 0);
        totalReceived += parseFloat(stat.received || 0);
      });
      
      setTotalBandwidth({ sent: totalSent.toFixed(1), received: totalReceived.toFixed(1) });
      
      // Update local upload stats (total of all outbound streams)
      setLocalUploadStats({ sent: totalSent.toFixed(1) });
    };
    calculateTotal();
  }, [streamStats]);

  useEffect(() => {
    if (!onBandwidthUpdate) return;
    onBandwidthUpdate(totalBandwidth);
  }, [onBandwidthUpdate, totalBandwidth]);

  useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      isCallActive,
      isOnCall,
      groupCallActive,
      groupCallType,
      videoEnabled,
      audioEnabled,
      hasLocalStream: !!localStream,
      showJoinButton,
      isInitiator: groupCallInitiator === currentUser?.peerId
    });
  }, [onStateChange, isCallActive, isOnCall, groupCallActive, groupCallType, videoEnabled, audioEnabled, localStream, showJoinButton, groupCallInitiator, currentUser?.peerId]);

  // Cleanup stats monitoring on unmount
  useEffect(() => {
    return () => {
      if (statsIntervalRef.current) {
        Object.values(statsIntervalRef.current).forEach(clearInterval);
      }
    };
  }, []);

  // Monitor for stale streams (no frames received for 15+ seconds)
  useEffect(() => {
    // Check every 5 seconds for stale streams
    staleStreamCheckRef.current = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 15000; // 15 seconds - allows time for reconnection
      
      Object.keys(remoteStreams).forEach(peerId => {
        const lastFrameTime = lastFrameTimeRef.current[peerId];
        const stream = remoteStreams[peerId];
        
        // Don't remove if stream has active video tracks (still connected)
        const hasActiveTracks = stream?.getVideoTracks().some(t => t.readyState === 'live') ||
                                stream?.getAudioTracks().some(t => t.readyState === 'live');
        
        if (lastFrameTime && (now - lastFrameTime) > staleThreshold && !hasActiveTracks) {
          console.log('[VideoCall] Stream stale for', peerId, '- no frames for', ((now - lastFrameTime) / 1000).toFixed(1), 'seconds');
          
          // Attempt re-call if we have an active local stream and the call still exists
          const existingCall = mediaCallsRef.current[peerId];
          if (localStreamRef.current && (isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) {
            console.log('[VideoCall] Attempting re-call for stale stream to:', peerId);
            // Clean up old call
            if (existingCall) {
              try { existingCall.close(); } catch(_) {}
              delete mediaCallsRef.current[peerId];
            }
            // Remove stale stream
            setRemoteStreams(prev => {
              const updated = { ...prev };
              delete updated[peerId];
              return updated;
            });
            delete lastFrameTimeRef.current[peerId];
            // Re-call
            const callType = callPeerTypesRef.current[peerId] || 'video';
            try {
              callPeer(peerId, localStreamRef.current, callType);
            } catch(e) {
              console.error('[VideoCall] Re-call failed for stale stream:', e);
            }
          } else {
            console.log('[VideoCall] No local stream, removing stale stream for:', peerId);
            // Remove stale stream
            setRemoteStreams(prev => {
              const updated = { ...prev };
              delete updated[peerId];
              return updated;
            });
            delete lastFrameTimeRef.current[peerId];
            if (existingCall) {
              try { existingCall.close(); } catch(_) {}
              delete mediaCallsRef.current[peerId];
            }
            // Clean up stats
            if (lastStatsRef.current[peerId]) {
              delete lastStatsRef.current[peerId];
            }
            if (statsIntervalRef.current?.[peerId]) {
              clearInterval(statsIntervalRef.current[peerId]);
              delete statsIntervalRef.current[peerId];
            }
            showToast(`${peerMetadata[peerId]?.userName || 'User'} left the call`, 'info');
          }
        } else if (lastFrameTime && (now - lastFrameTime) > 8000 && hasActiveTracks) {
          const staleSecs = (now - lastFrameTime) / 1000;
          // After 20 seconds with no frames but live tracks, the stream is effectively frozen — re-call
          if (staleSecs > 20 && localStreamRef.current && (isOnCallRef.current || isCallActiveRef.current || groupCallActiveRef.current)) {
            console.log('[VideoCall] Stream frozen for', peerId, '- no frames for', staleSecs.toFixed(1), 'seconds with live tracks, forcing re-call');
            const existingCall = mediaCallsRef.current[peerId];
            if (existingCall) {
              try { existingCall.close(); } catch(_) {}
              delete mediaCallsRef.current[peerId];
            }
            setRemoteStreams(prev => {
              const updated = { ...prev };
              delete updated[peerId];
              return updated;
            });
            delete lastFrameTimeRef.current[peerId];
            const callType = callPeerTypesRef.current[peerId] || 'video';
            try {
              callPeer(peerId, localStreamRef.current, callType);
            } catch(e) {
              console.error('[VideoCall] Re-call failed for frozen stream:', e);
            }
          } else {
            // Stream is slow but tracks are live - just log, don't remove
            console.log('[VideoCall] Stream slow for', peerId, '- no frames for', staleSecs.toFixed(1), 'seconds but tracks are live, keeping stream');
          }
        }
      });
    }, 5000); // Check every 5 seconds
    
    return () => {
      if (staleStreamCheckRef.current) {
        clearInterval(staleStreamCheckRef.current);
      }
    };
  }, [remoteStreams, peerMetadata]);

  // ── Picture-in-Picture via canvas compositing ──────────────────────────────
  // All participants are drawn onto a hidden <canvas> in a 2-column grid.
  // That canvas is captured as a MediaStream and fed to a hidden <video>,
  // which is then used for the browser's native requestPictureInPicture().

  const drawPIPCanvas = useCallback(() => {
    const canvas = pipCanvasRef.current;
    if (!canvas) return;

    const meId = currentUserRef.current?.peerId;
    const allUsers = (usersRef.current || []).filter(u => u?.peerId && u.status !== 'removed');

    // Build participant list: local first, then remote
    const participants = [
      {
        id: 'local',
        videoEl: localVideoRef.current,
        name: 'You',
        hasVideo: !!(localVideoRef.current?.srcObject && videoEnabledRef.current),
        micOn: !!audioEnabledRef.current,
        camOn: !!videoEnabledRef.current,
        initial: (currentUserRef.current?.initial || 'Y'),
        color: currentUserRef.current?.color,
        mirror: true,
      },
      ...allUsers.filter(u => u.peerId !== meId).map(u => {
        const el = remoteVideoRefs.current[u.peerId];
        const meta = peerMetadataRef.current?.[u.peerId];
        const name = meta?.userName || u.peerName || u.userName || u.initial || 'User';
        return {
          id: u.peerId,
          videoEl: el,
          name,
          hasVideo: !!(el?.srcObject && el.videoWidth > 0 && u.video?.enabled),
          micOn: !(u.audio?.muted),
          camOn: !!(u.video?.enabled),
          initial: u.initial || (name[0]?.toUpperCase() || 'U'),
          color: u.color,
          mirror: false,
        };
      }),
    ];

    const count = participants.length;
    const cols = count <= 1 ? 1 : 2;
    const rows = Math.ceil(count / cols);

    const PAD = 6;    // outer padding
    const GAP = 6;    // gap between cells
    const RADIUS = 8; // cell corner radius

    // Fixed portrait cell: 150×200 (3:4 ratio)
    const CELL_W = 150, CELL_H = 200;
    const targetW = PAD * 2 + cols * CELL_W + (cols - 1) * GAP;
    const targetH = PAD * 2 + rows * CELL_H + (rows - 1) * GAP;
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }

    const W = canvas.width;
    const H = canvas.height;

    const cellW = Math.floor((W - PAD * 2 - GAP * (cols - 1)) / cols);
    const cellH = Math.floor((H - PAD * 2 - GAP * (rows - 1)) / rows);

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, W, H);

    participants.forEach((p, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = PAD + col * (cellW + GAP);
      const y = PAD + row * (cellH + GAP);

      // Clip to rounded rect for this cell
      ctx.save();
      rrPath(ctx, x, y, cellW, cellH, RADIUS);
      ctx.clip();

      // Cell background
      ctx.fillStyle = '#1c1c1e';
      ctx.fillRect(x, y, cellW, cellH);

      // Video (cover-cropped, correct aspect ratio) or avatar fallback
      if (p.hasVideo && p.videoEl && p.videoEl.readyState >= 2) {
        try {
          ctx.save();
          if (p.mirror) {
            ctx.translate(x + cellW, y);
            ctx.scale(-1, 1);
            videoCover(ctx, p.videoEl, 0, 0, cellW, cellH);
          } else {
            videoCover(ctx, p.videoEl, x, y, cellW, cellH);
          }
          ctx.restore();
        } catch (_) {
          avatarFill(ctx, x, y, cellW, cellH, p.initial, p.color);
        }
      } else {
        avatarFill(ctx, x, y, cellW, cellH, p.initial, p.color);
      }

      // Gradient fade → name label at bottom
      const lH = 22;
      const g = ctx.createLinearGradient(x, y + cellH - lH * 2.2, x, y + cellH);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, 'rgba(0,0,0,0.78)');
      ctx.fillStyle = g;
      ctx.fillRect(x, y + cellH - lH * 2.2, cellW, lH * 2.2);

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(p.name, x + 7, y + cellH - 10);

      ctx.restore(); // release clip

      // Rounded border drawn outside clip so it appears on the cell edge
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 1;
      rrPath(ctx, x, y, cellW, cellH, RADIUS);
      ctx.stroke();

      // Mic + camera status badges at top-right (also outside clip)
      statusBadges(ctx, x + cellW, y, p.micOn, p.camOn);
    });

    // ── helpers (defined inside useCallback so they close over nothing external) ──

    function rrPath(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.arcTo(x + w, y,     x + w, y + r,     r);
      ctx.lineTo(x + w, y + h - r);
      ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
      ctx.lineTo(x + r, y + h);
      ctx.arcTo(x,     y + h, x,     y + h - r, r);
      ctx.lineTo(x, y + r);
      ctx.arcTo(x,     y,     x + r, y,          r);
      ctx.closePath();
    }

    // Draw video maintaining aspect ratio (object-fit: cover)
    function videoCover(ctx, el, x, y, w, h) {
      const vw = el.videoWidth, vh = el.videoHeight;
      if (!vw || !vh) { ctx.fillStyle = '#1c1c1e'; ctx.fillRect(x, y, w, h); return; }
      const vAR = vw / vh, cAR = w / h;
      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (vAR > cAR) { sw = vh * cAR; sx = (vw - sw) / 2; }
      else           { sh = vw / cAR; sy = (vh - sh) / 2; }
      ctx.drawImage(el, sx, sy, sw, sh, x, y, w, h);
    }

    // Draw avatar circle + initials when camera is off
    function avatarFill(ctx, x, y, w, h, initial, color) {
      if (color) {
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = color;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = 1.0;
      }
      const r = Math.min(w, h) * 0.22;
      ctx.globalAlpha = 0.32;
      ctx.fillStyle = color || '#4a4a5a';
      ctx.beginPath(); ctx.arc(x + w / 2, y + h / 2, r, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1.0;
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(r * 0.88, 10)}px system-ui, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(initial || 'U').slice(0, 2).toUpperCase(), x + w / 2, y + h / 2);
    }

    // Draw mic + camera status badge circles at top-right of a cell
    function statusBadges(ctx, rightX, topY, micOn, camOn) {
      const R = 8, P = 5, G = 4;

      // Camera badge (rightmost)
      const camCX = rightX - P - R;
      const camCY = topY + P + R;
      circleBadge(ctx, camCX, camCY, R, camOn ? '#34c759' : '#636366');
      drawCamIcon(ctx, camCX, camCY, R * 0.52, camOn);

      // Mic badge
      const micCX = rightX - P - R * 3 - G;
      const micCY = topY + P + R;
      circleBadge(ctx, micCX, micCY, R, micOn ? '#34c759' : '#ff3b30');
      drawMicIcon(ctx, micCX, micCY, R * 0.52, micOn);
    }

    function circleBadge(ctx, cx, cy, r, color) {
      ctx.globalAlpha = 0.93;
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1.0;
    }

    function drawCamIcon(ctx, cx, cy, s, on) {
      ctx.fillStyle = '#fff';
      // Camera body rectangle
      ctx.fillRect(cx - s, cy - s * 0.62, s * 1.6, s * 1.24);
      // Viewfinder triangle (left bump)
      ctx.beginPath();
      ctx.moveTo(cx - s,       cy - s * 0.28);
      ctx.lineTo(cx - s * 1.4, cy - s * 0.58);
      ctx.lineTo(cx - s * 1.4, cy + s * 0.28);
      ctx.closePath(); ctx.fill();
      // Lens circle (cut out with badge color)
      ctx.fillStyle = on ? '#34c759' : '#636366';
      ctx.globalAlpha = 0.93;
      ctx.beginPath(); ctx.arc(cx + s * 0.15, cy, s * 0.42, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1.0;
      if (!on) slash(ctx, cx, cy, s * 1.15);
    }

    function drawMicIcon(ctx, cx, cy, s, on) {
      const bW = s * 0.85, bH = s * 1.3, bR = bW / 2;
      ctx.fillStyle = '#fff';
      // Pill body: top arc + sides + bottom arc
      ctx.beginPath();
      ctx.arc(cx, cy - bH / 2 + bR, bR, Math.PI, 0);
      ctx.lineTo(cx + bW / 2, cy + bH / 2 - bR);
      ctx.arc(cx, cy + bH / 2 - bR, bR, 0, Math.PI);
      ctx.closePath(); ctx.fill();
      if (!on) slash(ctx, cx, cy, s * 1.15);
    }

    function slash(ctx, cx, cy, s) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.88)';
      ctx.lineWidth = 1.5; ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx - s, cy + s);
      ctx.lineTo(cx + s, cy - s);
      ctx.stroke();
      ctx.restore();
    }
  }, []);

  const tryEnterPIP = useCallback(async () => {
    if (!document.pictureInPictureEnabled) return;
    if (document.pictureInPictureElement) return; // already in PIP
    if (isPIPActiveRef.current) return;

    const canvas = pipCanvasRef.current;
    const video = pipVideoRef.current;
    if (!canvas || !video) return;

    // Draw first frame before capturing stream
    drawPIPCanvas();

    // Wire canvas → video if not already done
    if (!pipStreamRef.current || video.srcObject !== pipStreamRef.current) {
      pipStreamRef.current = canvas.captureStream(15); // 15 fps is plenty for PIP
      video.srcObject = pipStreamRef.current;
      try { await video.play(); } catch (_) {}
    }

    // Start the draw loop (~15 fps)
    if (pipDrawIntervalRef.current) clearInterval(pipDrawIntervalRef.current);
    pipDrawIntervalRef.current = setInterval(drawPIPCanvas, 66);

    // Give the video element time to receive frames before requesting PIP
    await new Promise(r => setTimeout(r, 250));

    try {
      await video.requestPictureInPicture();
      setIsPIPActive(true);
      isPIPActiveRef.current = true;
      console.log('[VideoCall] 📺 PIP started — showing all participants');
    } catch (e) {
      console.warn('[VideoCall] PIP request failed:', e.message);
      clearInterval(pipDrawIntervalRef.current);
      pipDrawIntervalRef.current = null;
    }
  }, [drawPIPCanvas]);

  const tryExitPIP = useCallback(async () => {
    if (!document.pictureInPictureElement) return;
    try {
      await document.exitPictureInPicture();
    } catch (e) {
      console.warn('[VideoCall] PIP exit failed:', e.message);
    }
    clearInterval(pipDrawIntervalRef.current);
    pipDrawIntervalRef.current = null;
    setIsPIPActive(false);
    isPIPActiveRef.current = false;
  }, []);

  // Auto-enter PIP when tab goes to background; auto-exit when user returns
  useEffect(() => {
    if (!isCallActive && !isOnCall) return;

    const onVisChange = () => {
      if (document.hidden) {
        tryEnterPIP();
      } else {
        setTimeout(() => {
          if (!document.hidden && isPIPActiveRef.current) tryExitPIP();
        }, 600);
      }
    };

    const onLeavePIP = () => {
      // User closed the PIP window manually
      clearInterval(pipDrawIntervalRef.current);
      pipDrawIntervalRef.current = null;
      setIsPIPActive(false);
      isPIPActiveRef.current = false;
    };

    document.addEventListener('visibilitychange', onVisChange);
    document.addEventListener('leavepictureinpicture', onLeavePIP);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      document.removeEventListener('leavepictureinpicture', onLeavePIP);
    };
  }, [isCallActive, isOnCall, tryEnterPIP, tryExitPIP]);
  // ── End Picture-in-Picture ───────────────────────────────────────────────

  const stopAllCalls = () => {
    console.log('[VideoCall] 🛑 END CALL BUTTON PRESSED');
    // Mark as intentional so call-close recovery doesn't auto re-call
    intentionalCloseRef.current = true;
    // Clear any pending queued calls and retry timers
    pendingCallsRef.current = {};
    Object.values(callCloseRetryTimersRef.current).forEach(t => clearTimeout(t));
    callCloseRetryTimersRef.current = {};
    // Exit PIP so the floating window doesn't linger after call ends
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(() => {});
    }
    clearInterval(pipDrawIntervalRef.current);
    pipDrawIntervalRef.current = null;
    setIsPIPActive(false);
    isPIPActiveRef.current = false;
    const myPeerId = currentUserRef.current?.peerId;
    const initiatorPeerId = groupCallInitiatorRef.current;
    const iAmInitiator = myPeerId && initiatorPeerId && myPeerId === initiatorPeerId;
    
    console.log('[VideoCall] Am I initiator?', iAmInitiator, '(me:', myPeerId, 'initiator:', initiatorPeerId, ')');
    
    // NEW BEHAVIOR: Anyone can leave without ending call for others
    // Call only ends when the LAST person leaves
    console.log('[VideoCall] 🟡 USER LEAVING CALL (others continue)');
    
    // Notify that this user is leaving (not ending for everyone)
    if (onCallEvent) {
      const userName = currentUserRef.current?.userName || 'You';
      onCallEvent(`${userName} left the call`, 'fa-phone-slash');
    }
    
    // Stop local stream only
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }

    // Stop the background effects processor and clear raw camera reference
    if (bgProcessorRef.current?.stop) {
      try { bgProcessorRef.current.stop(); } catch (_) {}
    }
    bgProcessorRef.current = null;
    rawCameraStreamRef.current = null;

    // Close my outgoing calls
    Object.entries(mediaCallsRef.current).forEach(([peerId, call]) => {
      try {
        call.close();
      } catch (e) {
        console.warn('[VideoCall] Error closing call:', e);
      }
    });
    mediaCallsRef.current = {};
    
    // Clear stats monitoring
    if (statsIntervalRef.current) {
      Object.entries(statsIntervalRef.current).forEach(([peerId, intervalId]) => {
        clearInterval(intervalId);
      });
      statsIntervalRef.current = {};
    }

    // Cleanup audio context
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {}
      audioContextRef.current = null;
    }

    // Clean up all remote video elements to prevent frozen frames
    Object.keys(remoteVideoRefs.current).forEach(pid => cleanupVideoElement(pid));
    // Clear all lastAssignedStreamRef entries so new calls won't be skipped by useEffect
    lastAssignedStreamRef.current = {};

    // Clear remote streams since we've closed our peer connections
    setRemoteStreams({});
    setDisconnectedPeers({});
    setVideoEnabled(false);
    setAudioEnabled(false);
    setIsCallActive(false);
    setIsOnCall(false);
    
    // Update refs
    videoEnabledRef.current = false;
    audioEnabledRef.current = false;
    isCallActiveRef.current = false;
    isOnCallRef.current = false;
    localStreamRef.current = null;
    
    // Show join button so user can rejoin if they want (check before resetting)
    if (groupCallActiveRef.current) {
      console.log('[VideoCall] User left call - showing join button to rejoin');
      setShowJoinButton(true);
    }
    
    // Reset group call refs — they will be re-established from socket events on rejoin
    groupCallActiveRef.current = false;
    
    // Remove self from peersOnCallRef since we're no longer on a call
    if (currentUser?.peerId) {
      delete peersOnCallRef.current[currentUser.peerId];
    }
    
    // Notify server that this user left the call
    if (connectionManager && currentUser) {
      connectionManager.broadcastData({
        type: 'call-state-update',
        data: {
          peerId: currentUser.peerId,
          videoCall: false,
          audioCall: false,
          timestamp: Date.now()
        }
      });

      // CRITICAL: Persist "left the call" state on the server so the per-user
      // video/audio status fields don't stay stale (used as a fallback signal
      // to detect when everyone has left the group call).
      const apiBase = process.env.REACT_APP_API_BASE || '';
      fetch(`${apiBase}/room/${connectionManager.roomId}/update-call-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          peerId: currentUser.peerId,
          video: { status: false, enabled: false, muted: true },
          audio: { status: false, muted: true }
        })
      }).catch(e => console.error('[VideoCall] Failed to update server call status on leave:', e));

      // Tell the server this peer left the group call so it can update the
      // participants list and end the call for everyone if nobody remains.
      broadcastGroupCallEnd();
    }
    
    if (onCallEvent) {
      onCallEvent('You left the call', 'fa-phone-slash');
    }
    
    showToast('You left the call', 'info');
  };

  return (
    <div className="video-call-container">
      {/* Control Buttons */}
      {!hideControls && (
        <div className="call-controls">
          {!isCallActive && !groupCallActive && (
            <>
              <button className="btn btn-primary" onClick={startVideoCall}>
                <i className="fas fa-video"></i> Start Video
              </button>
              <button className="btn btn-secondary" onClick={startAudioCall}>
                <i className="fas fa-microphone"></i> Start Audio
              </button>
            </>
          )}

          {!isCallActive && groupCallActive && !isOnCall && (
            <>
              <button className="btn btn-success" onClick={joinActiveGroupCall}>
                <i className={`fas ${groupCallType === 'video' ? 'fa-video' : 'fa-phone'}`}></i> Join {groupCallType} call
              </button>
            </>
          )}

          {(isCallActive || isOnCall) && (
            <>
              {/* If we have a local stream, show toggle buttons */}
              {localStream ? (
                <>
                  {localStream.getVideoTracks().length > 0 && (
                    <button
                      className={`btn ${videoEnabled ? 'btn-warning' : 'btn-secondary'}`}
                      onClick={toggleVideo}
                    >
                      <i className={`fas fa-${videoEnabled ? 'video' : 'video-slash'}`}></i>
                    </button>
                  )}
                  {localStream.getAudioTracks().length > 0 && (
                    <button
                      className={`btn ${audioEnabled ? 'btn-warning' : 'btn-secondary'}`}
                      onClick={toggleAudio}
                    >
                      <i className={`fas fa-${audioEnabled ? 'microphone' : 'microphone-slash'}`}></i>
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* No local stream - allow starting video/audio to become visible */}
                  <button className="btn btn-primary" onClick={startVideoCall}>
                    <i className="fas fa-video"></i> Start Video
                  </button>
                  <button className="btn btn-secondary" onClick={startAudioCall}>
                    <i className="fas fa-microphone"></i> Start Audio
                  </button>
                </>
              )}
              <button className="btn btn-danger" onClick={stopAllCalls}>
                <i className="fas fa-phone-slash"></i>
              </button>
              {typeof document !== 'undefined' && document.pictureInPictureEnabled && (
                <button
                  className={`btn ${isPIPActive ? 'btn-info' : 'btn-secondary'}`}
                  onClick={() => isPIPActive ? tryExitPIP() : tryEnterPIP()}
                  title={isPIPActive ? 'Exit Picture-in-Picture' : 'Pop out (Picture-in-Picture)'}
                >
                  <i className={`fas ${isPIPActive ? 'fa-compress-alt' : 'fa-external-link-alt'}`}></i>
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Incoming Call Popup */}
      {pendingCall && (
        <div className="incoming-call-popup">
          <div className="incoming-call-content">
            <div className="incoming-call-icon">
              <i className={`fas ${pendingCall.callType === 'video' ? 'fa-video' : 'fa-phone'}`}></i>
            </div>
            <h3>{pendingCall.isGroupCall ? 'Group' : 'Incoming'} {pendingCall.callType} call</h3>
            <p>{pendingCall.isGroupCall ? 'started by' : 'from'} <strong>{pendingCall.callerName}</strong></p>
            <div className="incoming-call-actions">
              <button className="btn btn-success" onClick={acceptPendingCall}>
                <i className="fas fa-check"></i> {pendingCall.isGroupCall ? 'Join' : 'Accept'}
              </button>
              <button className="btn btn-danger" onClick={declinePendingCall}>
                <i className="fas fa-times"></i> {pendingCall.isGroupCall ? 'Later' : 'Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Participant Grid */}
      {groupCallActive && (
        <div className={`participant-grid ${users?.length > 4 ? 'two-rows' : 'one-row'}`}>
          {(() => {
            const meId = currentUser?.peerId;
            const activeUsers = (users || []).filter(u => u && u.peerId && u.status !== 'removed');
            const maxSlots = activeUsers.length >= 5 ? 8 : 4;
            const others = activeUsers.filter(u => u.peerId !== meId);
            const ordered = [{ peerId: meId, isLocal: true }, ...others.map(u => ({ peerId: u.peerId, isLocal: false }))];
            const slots = Array.from({ length: maxSlots }, (_, i) => ordered[i] || null);

            return slots.map((slot, idx) => {
              const isLocal = !!slot?.isLocal;
              const peerId = slot?.peerId;
              const user = isLocal
                ? currentUser
                : (users || []).find(u => u.peerId === peerId);

              const displayName = isLocal
                ? 'You'
                : (peerMetadata[peerId]?.userName || user?.userName || user?.peerName || user?.initial || 'User');

              const initial = isLocal
                ? (currentUser?.initial || 'Y')
                : (user?.initial || (displayName?.[0] || 'U'));

              const userColor = isLocal
                ? (currentUser?.color || '#0084ff')
                : (user?.color || '#0084ff');

              const stream = !isLocal && peerId ? remoteStreams[peerId] : null;
              
              // Simple logic: show video if stream exists (for remote) or localStream + videoEnabled (for local)
              // Use server state user.video.enabled to determine if we should show video or initials
              const showVideo = isLocal
                ? !!(localStream && videoEnabled)
                : !!(stream && user?.video?.enabled);

              const micOn = isLocal ? !!audioEnabled : !(user?.audio?.muted);
              const camOn = isLocal ? !!videoEnabled : !!(user?.video?.enabled);

              // User left detection - check both user status and disconnectedPeers
              const hasLeft = !isLocal && (user?.status === 'left' || !!disconnectedPeers[peerId]);

              // Voice activity level for this participant
              // Only show speaking indicator when mic is actually ON
              const voiceLevel = isLocal ? (audioLevels['local'] || 0) : (audioLevels[peerId] || 0);
              const isSpeaking = voiceLevel > 0 && micOn && !hasLeft;

              // Convert hex color to rgba for glow effects
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
              };

              // Dynamic glow style for the tile border using user's color
              const tileGlowStyle = isSpeaking ? {
                boxShadow: `inset 0 0 0 ${2 + voiceLevel * 3}px ${hexToRgba(userColor, 0.3 + voiceLevel * 0.5)}, 0 0 ${voiceLevel * 12}px ${hexToRgba(userColor, voiceLevel * 0.4)}`
              } : {};

              // Dynamic glow style for the initials circle using user's color
              const initialGlowStyle = isSpeaking ? {
                boxShadow: `0 0 ${4 + voiceLevel * 16}px ${2 + voiceLevel * 6}px ${hexToRgba(userColor, 0.25 + voiceLevel * 0.45)}`,
                borderColor: hexToRgba(userColor, 0.4 + voiceLevel * 0.6)
              } : {};

              return (
                <div
                  key={peerId || `empty-${idx}`}
                  className={`participant-tile${isSpeaking ? ' speaking' : ''}`}
                  style={{
                    ...tileGlowStyle,
                    '--user-color': userColor
                  }}
                >
                  {slot ? (
                    <>
                      {hasLeft ? (
                        <div className="participant-placeholder participant-left">
                          <div
                            className="participant-initial"
                            style={{ opacity: 0.4, background: 'rgba(255,255,255,0.06)' }}
                          >
                            {String(initial).slice(0, 2)}
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '6px', fontWeight: 600 }}>User left</div>
                        </div>
                      ) : showVideo ? (
                        <video
                          className="participant-video"
                          ref={el => {
                            if (!el) return;
                            if (isLocal) {
                              localVideoRef.current = el;
                              // Ensure srcObject is always set for local video
                              if (localStreamRef.current && el.srcObject !== localStreamRef.current) {
                                el.srcObject = localStreamRef.current;
                                el.muted = true; // Explicitly mute to prevent echo
                                el.volume = 0; // Set volume to 0 as additional safeguard
                                el.play().catch(() => {});
                              }
                            } else if (peerId) {
                              remoteVideoRefs.current[peerId] = el;
                              // Clear srcObject if user has left or stream is null
                              if (hasLeft || !stream) {
                                el.srcObject = null;
                              } else if (stream && el.srcObject !== stream) {
                                el.srcObject = stream;
                                el.muted = false; // Ensure remote audio is not muted
                                el.volume = 1; // Full volume for remote audio
                                el.play().catch(() => {});
                              }
                            }
                          }}
                          autoPlay
                          muted={isLocal}
                          playsInline
                        />
                      ) : (
                        <>
                          {/* Hidden video element for audio-only mode to prevent echo */}
                          {(isLocal ? localStream : stream) && (
                            <video
                              ref={el => {
                                if (!el) return;
                                if (isLocal) {
                                  localVideoRef.current = el;
                                  if (localStreamRef.current && el.srcObject !== localStreamRef.current) {
                                    el.srcObject = localStreamRef.current;
                                    el.muted = true; // Explicitly mute to prevent echo
                                    el.volume = 0; // Set volume to 0 as additional safeguard
                                    el.play().catch(() => {});
                                  }
                                } else if (peerId) {
                                  remoteVideoRefs.current[peerId] = el;
                                  if (stream && el.srcObject !== stream) {
                                    el.srcObject = stream;
                                    el.muted = false; // Ensure remote audio is not muted
                                    el.volume = 1; // Full volume for remote audio
                                    el.play().catch(() => {});
                                  }
                                }
                              }}
                              autoPlay
                              muted={isLocal}
                              playsInline
                              style={{ display: 'none' }}
                            />
                          )}
                          <div className="participant-placeholder">
                            <div
                              className={`participant-initial${isSpeaking ? ' speaking' : ''}`}
                              style={initialGlowStyle}
                            >
                              {String(initial).slice(0, 2)}
                            </div>
                          </div>
                        </>
                      )}

                      <div className="participant-footer">
                        <div className="participant-name" style={hasLeft ? { color: 'rgba(255,255,255,0.5)' } : {}}>{displayName}</div>
                        <div className="participant-badges">
                          <i className={`fas ${hasLeft ? 'fa-microphone-slash' : (micOn ? 'fa-microphone' : 'fa-microphone-slash')}`} style={hasLeft ? { color: '#ff3b30' } : {}}></i>
                          <i className={`fas ${hasLeft ? 'fa-video-slash' : (camOn ? 'fa-video' : 'fa-video-slash')}`} style={hasLeft ? { color: '#ff3b30' } : {}}></i>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="participant-empty">
                      <div className="participant-empty-text">No User</div>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
      {/* ── Hidden PIP compositing elements (do not remove) ── */}
      <canvas
        ref={pipCanvasRef}
        width={640}
        height={360}
        style={{ display: 'none', position: 'absolute', pointerEvents: 'none' }}
        aria-hidden="true"
      />
      <video
        ref={pipVideoRef}
        muted
        playsInline
        style={{ display: 'none', position: 'absolute', pointerEvents: 'none' }}
        aria-hidden="true"
      />
    </div>
  );
});

export default VideoCall;
